import { useEffect, useState } from "react";
import { Routes, Route, Navigate, Link, useParams } from "react-router";
import {
  buildLessonOutputTarget,
  type CourseEngineOutputTarget,
  type LessonCompletionCriteria,
} from "@ease/page-builder/courses";
import { useApi } from "@/hooks/use-api";
import { Skeleton } from "@/components/skeleton";
import { getStoredActiveTenantId } from "@/lib/api-client";
import { useAuth } from "@/lib/auth-provider";

interface CourseCatalogItem {
  id: string;
  slug: string;
  title: string;
  description: string | null;
  status: string;
  sortOrder: number;
  publishedAt: string | null;
  createdAt: string;
  updatedAt: string;
  moduleCount: number;
  lessonCount: number;
  linkedPageCount: number;
  videoLessonCount: number;
  externalVideoLessonCount: number;
  missingVideoAssetCount: number;
}

interface CourseCatalogResponse {
  routeId: "stero.courses.catalog.v1";
  readOnly: boolean;
  catalogVersion: "stero.course-catalog.v1";
  counts: {
    courses: number;
    visibleCourses: number;
  };
  pagination: {
    limit: number;
    offset: number;
    total: number;
  };
  items: CourseCatalogItem[];
}

interface CourseStructureLesson {
  id: string;
  title: string;
  lessonType: string;
  sortOrder: number;
  estimatedMinutes: number | null;
  contentPage: {
    id: string;
    slug: string | null;
    title: string | null;
    status: string | null;
  } | null;
  assets: {
    hasExternalVideo: boolean;
    videoDelivery: {
      provider: string;
      embedUrl: string;
      isSandboxed: boolean;
    } | null;
    hasPdf: boolean;
    hasQuiz: boolean;
  };
}

interface CourseStructureModule {
  id: string;
  title: string;
  sortOrder: number;
  lessons: CourseStructureLesson[];
}

interface CourseStructureResponse {
  routeId: "stero.courses.structure.v1";
  readOnly: boolean;
  structureVersion: "stero.course-structure.v1";
  course: CourseCatalogItem & {
    accessTierIds: string[];
  };
  modules: CourseStructureModule[];
}

interface CourseProgressLesson {
  id: string;
  title: string;
  moduleTitle: string;
  lessonType: string;
  startedCount: number;
  startedLearnerCount: number;
  completionCount: number;
  completionRatePercent: number;
  startToCompletionRatePercent: number;
  quizAttempts: number;
  quizPassRatePercent: number;
  exerciseSubmissions: number;
  exerciseSubmitterCount: number;
}

interface CourseProgressModule {
  id: string;
  title: string;
  sortOrder: number;
  lessonCount: number;
  startedEvents: number;
  startedLearnerLessons: number;
  completionCount: number;
  completionRatePercent: number;
  startToCompletionRatePercent: number;
  quizAttempts: number;
  exerciseSubmissions: number;
}

interface CourseProgressResponse {
  routeId: "stero.courses.progress-summary.v1";
  readOnly: boolean;
  progressVersion: "stero.course-progress-summary.v1";
  course: {
    id: string;
    slug: string;
    title: string;
    status: string;
  };
  counts: {
    enrollments: number;
    active: number;
    completed: number;
    dropped: number;
  };
  progressBuckets: Array<{
    label: string;
    count: number;
  }>;
  courseActivity: {
    courseCompletionEvents: number;
    courseCompletedLearners: number;
  };
  modules: CourseProgressModule[];
  lessons: CourseProgressLesson[];
}

interface CourseLearnerOperationsResponse {
  routeId: "stero.courses.learner-operations.v1";
  readOnly: true;
  operationsVersion: "stero.course-learner-operations.v1";
  audience: "staff_learner_operations_review";
  decision: "READY_FOR_LEARNER_OPERATIONS_REVIEW" | "BLOCKED_LEARNER_OPERATIONS_INCONSISTENT";
  course: {
    id: string;
    slug: string;
    title: string;
    status: string;
  };
  counts: {
    enrollments: number;
    active: number;
    completed: number;
    dropped: number;
    invalidStatus: number;
    progressOutOfRange: number;
    completedWithoutCompletedAt: number;
    completedProgressBelow100: number;
    progress100NotCompleted: number;
    notStartedActive: number;
    activeInProgress: number;
    activeNearCompletion: number;
    activeWithActivity: number;
    activeWithoutActivity: number;
    recentEnrollments7d: number;
    recentEnrollments30d: number;
  };
  riskSegments: {
    atRiskNotStarted7d: number;
    atRiskStalled14d: number;
    needsNudge: number;
    readyForCompletionNudge: number;
  };
  activity: {
    lessonStartedEvents: number;
    lessonStartedLearners: number;
    lessonCompletedLearners: number;
    quizAttempts: number;
    exerciseSubmissions: number;
  };
  checks: Array<{
    key: string;
    ok: boolean;
    severity: "blocker" | "notice";
    summary: string;
  }>;
  capabilities: {
    enrollmentReadEnabled: true;
    learnerActivityReadEnabled: true;
    learnerIdentityVisible: false;
    enrollmentWriteEnabled: false;
    progressWriteEnabled: false;
    assignmentWriteEnabled: false;
    certificateIssueEnabled: false;
    publicServingAllowed: false;
    steroShadowMode: true;
    englishEasePrimaryMode: true;
  };
  blockedActions: string[];
  blockers: string[];
}

interface CourseCapabilityMatrixResponse {
  routeId: "stero.courses.capability-matrix.v1";
  readOnly: true;
  matrixVersion: "stero.course-capability-matrix.v1";
  audience: "staff_lms_capability_review";
  decision: "READY_FOR_LMS_CAPABILITY_REVIEW" | "BLOCKED_LMS_CAPABILITY_EVIDENCE_INCOMPLETE";
  recommendedMode: "internal_staff_shadow";
  course: {
    id: string;
    slug: string;
    title: string;
    status: string;
  };
  summary: {
    total: number;
    readyRead: number;
    contractMounted: number;
    observed: number;
    blocked: number;
  };
  capabilities: Array<{
    key: string;
    label: string;
    state: "ready_read" | "contract_mounted" | "observed" | "blocked";
    evidence: Record<string, unknown>;
    blockedActions: string[];
  }>;
  blockedActions: string[];
  sourcePacket: {
    routeId: CourseReleaseCandidateReadPacketResponse["routeId"];
    version: CourseReleaseCandidateReadPacketResponse["releaseCandidatePacketVersion"];
    decision: CourseReleaseCandidateReadPacketResponse["decision"];
  };
}

interface CourseReadinessCheck {
  key: string;
  ok: boolean;
  severity: "blocker" | "notice";
  summary: string;
}

interface CourseReadinessResponse {
  routeId: "stero.courses.internal-readiness.v1";
  readOnly: boolean;
  readinessVersion: "stero.course-internal-readiness.v1";
  decision: "READY_FOR_INTERNAL_STAFF_REVIEW" | "BLOCKED_STRUCTURE_INCOMPLETE";
  recommendedMode: "internal_staff_shadow";
  servingAllowed: false;
  learnerServingAllowed: false;
  targetWriteBehavior: "none";
  course: {
    id: string;
    slug: string;
    title: string;
    status: string;
  };
  counts: {
    modules: number;
    lessons: number;
    linkedPages: number;
    draftPages: number;
    publishedPages: number;
    videoLessons: number;
    externalVideoLessons: number;
    missingContentPages: number;
    missingVideoAssets: number;
    missingPdfAssets: number;
    missingQuizConfigs: number;
    unresolvedLinkedPages: number;
    unknownLessonTypes: number;
  };
  enrollmentSummary: {
    counts: {
      enrollments: number;
      active: number;
      completed: number;
      dropped: number;
    };
    progressBuckets: CourseProgressResponse["progressBuckets"];
  };
  checks: CourseReadinessCheck[];
  blockers: string[];
}

interface CourseCertificateReadinessResponse {
  routeId: "stero.courses.certificate-readiness.v1";
  readOnly: true;
  certificateReadinessVersion: "stero.course-certificate-readiness.v1";
  audience: "staff_certificate_readiness_review";
  decision: "READY_FOR_CERTIFICATE_READINESS_REVIEW" | "BLOCKED_CERTIFICATE_RECORD_INCONSISTENT";
  course: {
    id: string;
    slug: string;
    title: string;
    status: string;
  };
  counts: {
    enrollments: number;
    completedEnrollments: number;
    certificateTokensPresent: number;
    completedMissingCertificateToken: number;
    tokenWithoutCompletedStatus: number;
    certificateGeneratedEvents: number;
  };
  checks: Array<{
    key: string;
    ok: boolean;
    severity: "blocker" | "notice";
    summary: string;
  }>;
  capabilities: {
    learnerEligibilityReadEnabled: boolean;
    certificateTokenPresenceReadEnabled: boolean;
    tokenValuesVisible: false;
    certificateIssueEnabled: false;
    publicVerificationEnabled: false;
    publicVerificationRouteMounted: false;
    steroShadowMode: true;
    englishEasePrimaryMode: true;
  };
  blockedActions: string[];
  blockers: string[];
}

type CourseReviewState = "approved" | "changes_requested" | "pending" | "missing" | "not_applicable";

interface CourseReviewPacketLesson {
  id: string;
  title: string;
  lessonType: string;
  sortOrder: number;
  module: {
    id: string;
    title: string;
    sortOrder: number;
  };
  page: {
    id: string;
    slug: string | null;
    title: string | null;
    status: string | null;
    currentVersionId: string | null;
    currentVersionNumber: number | null;
  } | null;
  review: {
    id: string;
    status: "approved" | "changes_requested" | "pending";
    requestedAt: string | null;
    lastDecisionAt: string | null;
  } | null;
  reviewState: CourseReviewState;
  blockers: string[];
}

interface CourseReviewPacketResponse {
  routeId: "stero.courses.staff-review-packet.v1";
  readOnly: boolean;
  reviewPacketVersion: "stero.course-staff-review-packet.v1";
  decision: "READY_FOR_STAFF_PAGE_REVIEW" | "BLOCKED_STAFF_REVIEW_INCOMPLETE";
  recommendedMode: "internal_staff_shadow";
  servingAllowed: false;
  learnerServingAllowed: false;
  targetWriteBehavior: "none";
  course: {
    id: string;
    slug: string;
    title: string;
    status: string;
  };
  counts: {
    lessons: number;
    lessonsWithPages: number;
    approvedCurrentReviews: number;
    pendingCurrentReviews: number;
    changesRequested: number;
    missingCurrentReviews: number;
    notApplicable: number;
  };
  blockers: string[];
  lessons: CourseReviewPacketLesson[];
}

type CourseVideoDeliveryStatus = "trusted_embed" | "sandboxed_embed" | "missing_asset" | "invalid_asset";
type CourseVideoDeliveryRequiredAction = "none" | "mount_video_asset" | "mount_safe_embed_url" | "manual_provider_review";

interface CourseVideoDeliveryLesson {
  id: string;
  title: string;
  lessonType: "video";
  sortOrder: number;
  estimatedMinutes: number | null;
  module: {
    id: string;
    title: string;
    sortOrder: number;
  };
  page: {
    id: string;
    slug: string | null;
    title: string | null;
    status: string | null;
  } | null;
  delivery: {
    status: CourseVideoDeliveryStatus;
    provider: string | null;
    sourceProviderHint: string | null;
    embedUrl: string | null;
    isSandboxed: boolean;
    previewAllowed: boolean;
    manualReviewRequired: boolean;
    sourceVisible: false;
    requiredAction: CourseVideoDeliveryRequiredAction;
    operatorMessage: string;
    reasons: string[];
    blockers: string[];
  };
  blockers: string[];
}

interface CourseVideoDeliveryResponse {
  routeId: "stero.courses.video-delivery.v1";
  readOnly: boolean;
  deliveryVersion: "stero.course-video-delivery.v1";
  decision: "READY_FOR_VIDEO_DELIVERY_REVIEW" | "BLOCKED_VIDEO_DELIVERY_INCOMPLETE";
  recommendedMode: "internal_staff_shadow";
  servingAllowed: false;
  learnerServingAllowed: false;
  targetWriteBehavior: "none";
  course: {
    id: string;
    slug: string;
    title: string;
    status: string;
  };
  summary: {
    videoLessons: number;
    trustedEmbeds: number;
    sandboxedEmbeds: number;
    missingAssets: number;
    invalidAssets: number;
    manualReviewRequired: number;
    providers: string[];
    requiredActionCounts?: {
      none: number;
      mountVideoAsset: number;
      mountSafeEmbedUrl: number;
      manualProviderReview: number;
    };
  };
  videoAssetMountPlan?: {
    planVersion: "stero.course-video-asset-mount-plan.v1";
    state: "READY_FOR_STAFF_VIDEO_REVIEW" | "MOUNT_SAFE_VIDEO_ASSETS_REQUIRED";
    requiredActionCounts: {
      none: number;
      mountVideoAsset: number;
      mountSafeEmbedUrl: number;
      manualProviderReview: number;
    };
    instructions: string[];
  } | null;
  blockers: string[];
  lessons: CourseVideoDeliveryLesson[];
}

interface CourseLessonPreviewBlock {
  index: number;
  type: string;
  blockRef: string | null;
  textPreview: string | null;
  safeTextKeys: string[];
  hasMediaReference: boolean;
  mediaReferenceCount: number;
  redactedFieldCount: number;
}

interface CourseLessonPreviewResponse {
  routeId: "stero.courses.lesson-internal-preview.v1";
  readOnly: boolean;
  previewVersion: "stero.course-lesson-internal-preview.v1";
  decision: "READY_FOR_INTERNAL_LESSON_PREVIEW" | "BLOCKED_LESSON_PREVIEW_UNAVAILABLE";
  previewMode: "safe_block_outline";
  recommendedMode: "internal_staff_shadow";
  servingAllowed: false;
  learnerServingAllowed: false;
  targetWriteBehavior: "none";
  rawPageDataVisible: false;
  course: {
    id: string;
    slug: string;
    title: string;
    status: string;
  };
  lesson: {
    id: string;
    title: string;
    lessonType: string;
    sortOrder: number;
    estimatedMinutes: number | null;
    module: {
      id: string;
      title: string;
      sortOrder: number;
    };
  };
  page: {
    id: string;
    slug: string | null;
    title: string | null;
    status: string | null;
    currentVersionId: string | null;
    currentVersionNumber: number | null;
  } | null;
  version: {
    id: string;
    number: number | null;
    generationSource: string | null;
    createdAt: string | null;
  } | null;
  summary: {
    blocks: number;
    blockTypes: string[];
    textPreviewBlocks: number;
    mediaReferenceBlocks: number;
    redactedFields: number;
    zoneKeys: number;
  };
  blockers: string[];
  blocks: CourseLessonPreviewBlock[];
}

interface CourseReleaseCandidateReadPacketResponse {
  routeId: "stero.courses.release-candidate-read-packet.v1";
  readOnly: boolean;
  releaseCandidatePacketVersion: "stero.course-release-candidate-read-packet.v1";
  audience: "staff_internal_review";
  decision: "READY_FOR_RELEASE_CANDIDATE_REVIEW" | "BLOCKED_RELEASE_CANDIDATE_EVIDENCE_INCOMPLETE";
  recommendedMode: "internal_staff_release_candidate_review";
  servingAllowed: false;
  learnerServingAllowed: false;
  targetWriteBehavior: "none";
  requiresSeparateServingApproval: true;
  coreShadowReadiness: {
    decision: "READY_FOR_CORE_LMS_SHADOW_REVIEW" | "BLOCKED_CORE_LMS_SHADOW_EVIDENCE_INCOMPLETE";
    recommendedMode: "internal_lms_shadow_dual_run_review";
    readPathReady: boolean;
    mediaDeliveryReady: boolean;
    servingAllowed: false;
    targetWriteBehavior: "none";
    blockers: string[];
    advisoryBlockers: string[];
    summary: string;
  };
  course: {
    id: string;
    slug: string;
    title: string;
    status: string;
  };
  evidence: {
    structure: {
      counts: {
        modules: number;
        lessons: number;
        linkedPages: number;
        lessonTypes: Record<string, number>;
      };
    };
    readiness: {
      decision: CourseReadinessResponse["decision"];
      blockers: string[];
      counts: CourseReadinessResponse["counts"];
    };
    review: {
      decision: CourseReviewPacketResponse["decision"];
      blockers: string[];
      counts: CourseReviewPacketResponse["counts"];
    };
    videoDelivery: {
      decision: CourseVideoDeliveryResponse["decision"];
      blockers: string[];
      summary: CourseVideoDeliveryResponse["summary"];
    };
    certificateReadiness: {
      decision: CourseCertificateReadinessResponse["decision"];
      blockers: string[];
      counts: CourseCertificateReadinessResponse["counts"];
      capabilities: CourseCertificateReadinessResponse["capabilities"];
    };
    progress: {
      counts: CourseProgressResponse["counts"];
      progressBuckets: CourseProgressResponse["progressBuckets"];
      modules: CourseProgressModule[];
      activity: {
        startedLessonEvents: number;
        startedLearnerLessons: number;
        completedLearnerLessons: number;
        courseCompletionEvents: number;
        courseCompletedLearners: number;
        quizAttempts: number;
        exerciseSubmissions: number;
        exerciseSubmitterLessons: number;
        lessonsWithStarts: number;
        exerciseLessonsWithSubmissions: number;
        startToCompletionRatePercent: number;
      };
    };
    learnerOperations: {
      decision: CourseLearnerOperationsResponse["decision"];
      blockers: string[];
      counts: CourseLearnerOperationsResponse["counts"];
      riskSegments: CourseLearnerOperationsResponse["riskSegments"];
      activity: CourseLearnerOperationsResponse["activity"];
      capabilities: CourseLearnerOperationsResponse["capabilities"];
    };
    quizRuntime: {
      configuredQuizLessons: number;
      learnerPreviewMode: "questions_and_options_only" | "not_applicable";
      learnerAttemptSummaryEnabled: true;
      quizSubmitEnabled: false;
      answerRevealEnabled: false;
      scoringEnabled: false;
      explanationRevealEnabled: false;
      certificateEligibilityReadEnabled: true;
      certificateIssueEnabled: false;
    };
    exerciseRuntime: {
      exerciseLessons: number;
      linkedExerciseLessons: number;
      publishedExerciseLessons: number;
      learnerReadMode: "sanitized_page_content" | "not_applicable";
      exerciseSubmitEnabled: false;
      exerciseProgressWriteEnabled: false;
      scoringEnabled: false;
      answerRevealEnabled: false;
    };
    learnerPortalCapabilities: {
      courseDashboardReadEnabled: boolean;
      lessonContentReadEnabled: boolean;
      learnerActivityTimelineReadEnabled: boolean;
      learnerOperationsReadEnabled: boolean;
      learnerIdentityVisible: false;
      assignmentWriteEnabled: false;
      lessonActivityWriteEvidenceObserved: boolean;
      lessonCompletionWriteEvidenceObserved: boolean;
      courseCompletionWriteEvidenceObserved: boolean;
      courseCompletionActionContractMounted: boolean;
      quizSubmitWriteEvidenceObserved: boolean;
      exerciseSubmitWriteEvidenceObserved: boolean;
      quizPreviewReadEnabled: boolean;
      quizAttemptSummaryReadEnabled: boolean;
      quizSubmitActionContractMounted: boolean;
      exerciseRuntimeReadEnabled: boolean;
      exerciseSubmitActionContractMounted: boolean;
      certificateEligibilityReadEnabled: boolean;
      certificateTokenPresenceReadEnabled: boolean;
      certificateReadinessConsistent: boolean;
      certificateTokenValuesVisible: false;
      publicCertificateVerificationEnabled: false;
      publicServingAllowed: false;
      steroShadowMode: true;
      englishEasePrimaryMode: true;
    };
  };
  blockers: string[];
}

interface CourseLearnerServingPilotGateResponse {
  routeId: "stero.courses.learner-serving-pilot-gate.v1";
  readOnly: boolean;
  pilotGateVersion: "stero.course-learner-serving-pilot-gate.v1";
  audience: "staff_learner_serving_pilot_review";
  decision: "READY_FOR_SESSION_1_LEARNER_SERVING_PILOT_REVIEW" | "BLOCKED_RELEASE_CANDIDATE_EVIDENCE_INCOMPLETE";
  recommendedMode: "session_1_learner_serving_pilot_review";
  servingAllowed: false;
  learnerServingAllowed: false;
  targetWriteBehavior: "none";
  requiresExplicitPilotApproval: true;
  requiresSeparateRouteEnablementChange: true;
  requiresPostEnablementSmoke: true;
  course: {
    id: string;
    slug: string;
    title: string;
    status: string;
  };
  sourcePacket: {
    routeId: CourseReleaseCandidateReadPacketResponse["routeId"];
    version: CourseReleaseCandidateReadPacketResponse["releaseCandidatePacketVersion"];
    decision: CourseReleaseCandidateReadPacketResponse["decision"];
    recommendedMode: CourseReleaseCandidateReadPacketResponse["recommendedMode"];
  };
  evidence: {
    releaseCandidate: {
      blockers: string[];
      counts: {
        modules: number;
        lessons: number;
        linkedPages: number;
        videoLessons: number;
        trustedVideoEmbeds: number;
        approvedCurrentReviews: number;
        missingCurrentReviews: number;
        enrollments: number;
        activeLearners: number;
        completedLearners: number;
        learnerOperationsReadEnabled: boolean;
        learnerIdentityVisible: false;
        assignmentWriteEnabled: false;
        invalidEnrollmentStatuses: number;
        progressOutOfRange: number;
        activeWithoutActivity: number;
        activeNearCompletion: number;
        atRiskNotStarted7d: number;
        atRiskStalled14d: number;
        needsNudge: number;
        readyForCompletionNudge: number;
        startedLessonEvents: number;
        startedLearnerLessons: number;
        completedLearnerLessons: number;
        courseCompletionEvents: number;
        courseCompletedLearners: number;
        quizAttempts: number;
        exerciseSubmissions: number;
        exerciseSubmitterLessons: number;
        startToCompletionRatePercent: number;
        configuredQuizLessons: number;
        learnerAttemptSummaryEnabled: true;
        quizSubmitEnabled: false;
        certificateEligibilityReadEnabled: true;
        certificateIssueEnabled: false;
        certificateTokensPresent: number;
        completedMissingCertificateToken: number;
        tokenWithoutCompletedStatus: number;
        certificateGeneratedEvents: number;
        certificateTokenPresenceReadEnabled: boolean;
        certificateReadinessConsistent: boolean;
        certificateTokenValuesVisible: false;
        publicCertificateVerificationEnabled: false;
        exerciseLessons: number;
        publishedExerciseLessons: number;
        exerciseSubmitEnabled: false;
        exerciseProgressWriteEnabled: false;
        courseDashboardReadEnabled: boolean;
        lessonContentReadEnabled: boolean;
        nextLessonReadEnabled: boolean;
        learnerActivityTimelineReadEnabled: boolean;
        lessonActivityWriteEvidenceObserved: boolean;
        lessonCompletionWriteEvidenceObserved: boolean;
        courseCompletionWriteEvidenceObserved: boolean;
        courseCompletionActionContractMounted: boolean;
        learnerContinuationActionContractMounted: boolean;
        quizSubmitWriteEvidenceObserved: boolean;
        exerciseSubmitWriteEvidenceObserved: boolean;
        quizPreviewReadEnabled: boolean;
        quizAttemptSummaryReadEnabled: boolean;
        quizSubmitActionContractMounted: boolean;
        exerciseRuntimeReadEnabled: boolean;
        exerciseSubmitActionContractMounted: boolean;
        steroShadowMode: true;
        englishEasePrimaryMode: true;
      };
    };
    safety: {
      readOnly: boolean;
      servingAllowed: false;
      learnerServingAllowed: false;
      targetWriteBehavior: "none";
      rawPayloadsVisible: false;
      learnerIdentityVisible: false;
    };
  };
  approvalGates: Array<{
    key: string;
    status: "required";
    summary: string;
  }>;
  allowedNextActions: string[];
  blockedActions: string[];
  blockers: string[];
}

interface CourseCutoverReadinessSnapshotResponse {
  routeId: "stero.courses.cutover-readiness-snapshot.v1";
  readOnly: true;
  snapshotVersion: "stero.course-cutover-readiness-snapshot.v1";
  audience: "staff_cutover_review";
  decision: "READY_FOR_DUAL_RUN_OBSERVATION" | "BLOCKED_DUAL_RUN_EVIDENCE_INCOMPLETE";
  course: CourseLearnerServingPilotGateResponse["course"];
  learnerServingAllowed: false;
  publicCutoverAllowed: false;
  targetWriteBehavior: "none";
  dualRunStatus: {
    steroShadowMode: boolean;
    englishEasePrimaryMode: boolean;
    learnerOwnedWritesMounted: {
      lessonActivity: boolean;
      lessonCompletion: boolean;
      courseCompletion: boolean;
      assignment: boolean;
      quizSubmit: boolean;
      exerciseSubmit: boolean;
      exerciseProgress: boolean;
      certificateIssue: boolean;
    };
    learnerOwnedReadsMounted: {
      courseDashboard: boolean;
      lessonContent: boolean;
      nextLesson: boolean;
      learnerActivityTimeline: boolean;
      learnerOperations: boolean;
      courseCompletionActionContract: boolean;
      learnerContinuationActionContract: boolean;
      exerciseContent: boolean;
      exerciseSubmitActionContract: boolean;
      quizPreview: boolean;
      quizAttemptSummary: boolean;
      quizSubmitActionContract: boolean;
      certificateEligibility: true;
      certificateTokenPresence: boolean;
      certificateReadiness: boolean;
    };
  };
  evidence: {
    releaseCandidateDecision: CourseReleaseCandidateReadPacketResponse["decision"];
    pilotGateDecision: CourseLearnerServingPilotGateResponse["decision"];
    counts: CourseLearnerServingPilotGateResponse["evidence"]["releaseCandidate"]["counts"];
    safety: CourseLearnerServingPilotGateResponse["evidence"]["safety"];
  };
  blockers: string[];
  allowedNextActions: string[];
}

interface CourseDualRunSmokePacketResponse {
  routeId: "stero.courses.dual-run-smoke-packet.v1";
  readOnly: true;
  smokePacketVersion: "stero.course-dual-run-smoke-packet.v1";
  audience: "staff_dual_run_smoke_review";
  decision: "READY_FOR_DUAL_RUN_SMOKE_OBSERVATION" | "BLOCKED_DUAL_RUN_SMOKE_EVIDENCE_INCOMPLETE";
  course: CourseCutoverReadinessSnapshotResponse["course"];
  mode: {
    steroShadowMode: boolean;
    existingRuntimePrimaryMode: boolean;
    learnerServingAllowed: false;
    publicCutoverAllowed: false;
    targetWriteBehavior: "none";
    cutoverClaim: "blocked";
  };
  smokeSummary: {
    requiredReadsReady: boolean;
    learnerActivityObserved: boolean;
    learnerIdentityHidden: boolean;
    writeLocksHeld: boolean;
    publicServingBlocked: boolean;
    learnerContinuationReady: boolean;
    exerciseSubmissionReady: boolean;
    quizPreviewReady: boolean;
    observedLearnerLessons: number;
    observedLessonCompletions: number;
    observedCourseCompletions: number;
    observedQuizAttempts: number;
    observedExerciseSubmissions: number;
    learnerOperationsReady: boolean;
    certificateReadinessReady: boolean;
  };
  surfaces: {
    requiredReads: CourseCutoverReadinessSnapshotResponse["dualRunStatus"]["learnerOwnedReadsMounted"];
    observedLearnerOwnedSignals: {
      lessonActivity: boolean;
      lessonCompletion: boolean;
      courseCompletion: boolean;
      quizSubmitEvidence: boolean;
      exerciseSubmit: boolean;
    };
    lockedWrites: {
      assignment: boolean;
      quizSubmit: boolean;
      exerciseSubmit: boolean;
      exerciseProgress: boolean;
      certificateIssue: boolean;
      targetWrites: boolean;
    };
  };
  sourceSnapshot: {
    routeId: CourseCutoverReadinessSnapshotResponse["routeId"];
    version: CourseCutoverReadinessSnapshotResponse["snapshotVersion"];
    decision: CourseCutoverReadinessSnapshotResponse["decision"];
    pilotGateDecision: CourseLearnerServingPilotGateResponse["decision"];
    releaseCandidateDecision: CourseReleaseCandidateReadPacketResponse["decision"];
  };
  checks: Array<{
    key: string;
    ok: boolean;
    status: "passed" | "blocker" | "notice";
    severity: "blocker" | "notice";
    summary: string;
  }>;
  blockers: string[];
  requiredCutoverApprovals: string[];
  blockedActions: string[];
  allowedNextActions: string[];
}

interface WorkspaceTenant {
  id: string;
  slug: string;
  name: string;
  status: string;
  plan: string;
  permissionLevel: string;
  accessSource: string;
  enabledModules?: string[];
}

interface WorkspaceContextResponse {
  activeTenant: WorkspaceTenant | null;
  tenants: WorkspaceTenant[];
}

const COURSE_TENANT_STORAGE_KEY = "stero.courseTenantId";
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

function getStoredCourseTenantId(): string | null {
  if (typeof window === "undefined") return null;
  const value = window.localStorage.getItem(COURSE_TENANT_STORAGE_KEY)?.trim() ?? "";
  if (value && !UUID_RE.test(value)) {
    window.localStorage.removeItem(COURSE_TENANT_STORAGE_KEY);
    return null;
  }
  return value.length > 0 ? value : null;
}

function setStoredCourseTenantId(tenantId: string | null): void {
  if (typeof window === "undefined") return;
  if (tenantId?.trim()) {
    window.localStorage.setItem(COURSE_TENANT_STORAGE_KEY, tenantId.trim());
  } else {
    window.localStorage.removeItem(COURSE_TENANT_STORAGE_KEY);
  }
}

function describeTenant(tenant: WorkspaceTenant | null | undefined): string {
  if (!tenant) return "No tenant selected";
  return `${tenant.name} (${tenant.slug})`;
}

function hasCourseWorkspaceModule(tenant: WorkspaceTenant | null | undefined): boolean {
  return tenant?.enabledModules?.some((module) => {
    const normalized = module.toLowerCase();
    return normalized === "courses" || normalized === "learning";
  }) ?? false;
}

function isAssignedTenant(tenant: WorkspaceTenant): boolean {
  return tenant.accessSource === "assignment"
    || tenant.accessSource === "cross_org_assignment"
    || tenant.accessSource === "collaboration";
}

function selectCourseCatalogTenant(
  workspace: WorkspaceContextResponse | null | undefined,
  requestedCourseTenantId: string | null = getStoredCourseTenantId() ?? getStoredActiveTenantId(),
): WorkspaceTenant | null {
  if (!workspace) return null;

  const tenants = workspace.tenants ?? [];
  const requestedCourseTenant = requestedCourseTenantId
    ? tenants.find((tenant) => tenant.id === requestedCourseTenantId) ?? null
    : null;
  const assignedCourseTenant = tenants.find((tenant) => hasCourseWorkspaceModule(tenant) && isAssignedTenant(tenant)) ?? null;
  if (requestedCourseTenant && (!assignedCourseTenant || isAssignedTenant(requestedCourseTenant))) {
    return workspace.activeTenant?.id === requestedCourseTenant.id ? workspace.activeTenant : requestedCourseTenant;
  }

  const activeTenant = workspace.activeTenant ?? null;
  if (assignedCourseTenant) {
    return workspace.activeTenant?.id === assignedCourseTenant.id ? workspace.activeTenant : assignedCourseTenant;
  }

  if (hasCourseWorkspaceModule(activeTenant)) {
    return activeTenant;
  }

  const courseTenant = tenants.find(hasCourseWorkspaceModule) ?? null;
  return courseTenant ?? activeTenant;
}

function formatSnakeCaseLabel(value: string | null | undefined, fallback: string): string {
  return typeof value === "string" && value.trim() ? value.replace(/_/g, " ") : fallback;
}

function formatVideoProvider(provider: string | null | undefined): string {
  return formatSnakeCaseLabel(provider, "not mounted");
}

type CourseLessonType = LessonCompletionCriteria["lessonType"];

const COURSE_LESSON_TYPES = new Set<string>(["video", "text", "quiz", "exercise", "pdf"]);
const STAFF_COURSE_PREVIEW_ROLES = new Set(["super_admin", "admin", "customer_success", "staff"]);

function readCourseLessonType(value: string): CourseLessonType | null {
  return COURSE_LESSON_TYPES.has(value) ? value as CourseLessonType : null;
}

function canUseInternalLessonPreview(role: string | null | undefined): boolean {
  return STAFF_COURSE_PREVIEW_ROLES.has(role?.trim().toLowerCase() ?? "");
}

function outputKeyForTarget(target: CourseEngineOutputTarget): string {
  return target.kind === "lesson" ? "lesson" : "certificate";
}

function buildCourseEditorHref(target: CourseEngineOutputTarget, surface: "editor" | "review" = "editor"): string {
  const search = new URLSearchParams({
    source: "course-detail",
    pageType: target.pageType,
    outputKey: outputKeyForTarget(target),
    outputKind: target.kind,
    courseId: target.courseId,
  });

  if (target.moduleId) search.set("moduleId", target.moduleId);
  if (target.lessonId) search.set("lessonId", target.lessonId);
  if (target.lessonType) search.set("lessonType", target.lessonType);

  const path = target.pageId
    ? `/pages/editor/${encodeURIComponent(target.pageId)}`
    : "/pages/editor/new";

  return `${surface === "review" ? `${path}/review` : path}?${search.toString()}`;
}

function formatReviewState(state: CourseReviewState): string {
  return formatSnakeCaseLabel(state, "unknown");
}

function formatDeliveryStatus(state: CourseVideoDeliveryStatus | null | undefined): string {
  return formatSnakeCaseLabel(state, "unknown");
}

function formatVideoRequiredAction(action: CourseVideoDeliveryRequiredAction | null | undefined): string {
  return formatSnakeCaseLabel(action, "not specified");
}

function buildStaffReviewHref(courseId: string, lesson: CourseReviewPacketLesson): string | null {
  if (!lesson.page?.id) return null;

  const search = new URLSearchParams({
    source: "course-review-packet",
    courseId,
    moduleId: lesson.module.id,
    lessonId: lesson.id,
    lessonType: lesson.lessonType,
  });

  if (lesson.review?.id) search.set("reviewId", lesson.review.id);

  return `/pages/editor/${encodeURIComponent(lesson.page.id)}/review?${search.toString()}`;
}

function VideoDeliveryPreview({ lesson }: { lesson: CourseStructureLesson }) {
  const delivery = lesson.assets.videoDelivery;
  if (!delivery) return null;

  return (
    <div className="mt-3 overflow-hidden rounded-2xl border border-[var(--color-border)] bg-[var(--color-surface)]">
      <div className="flex flex-wrap items-center justify-between gap-2 border-b border-[var(--color-border)] px-3 py-2 text-xs">
        <span className="font-semibold capitalize text-[var(--color-foreground)]">
          {formatVideoProvider(delivery.provider)} delivery
        </span>
        <span className="rounded-full bg-background px-2 py-1 text-[var(--color-muted-foreground)]">
          {delivery.isSandboxed ? "sandboxed preview" : "trusted embed"}
        </span>
      </div>
      <div className="aspect-video bg-black">
        <iframe
          src={delivery.embedUrl}
          title={`${lesson.title} video preview`}
          loading="lazy"
          sandbox={delivery.isSandboxed ? "allow-scripts allow-same-origin allow-presentation" : undefined}
          allow="autoplay; encrypted-media; picture-in-picture"
          allowFullScreen
          className="h-full w-full border-0"
        />
      </div>
    </div>
  );
}

function ProgressSummaryPanel({
  progress,
  isLoading,
  error,
}: {
  progress: CourseProgressResponse | null | undefined;
  isLoading: boolean;
  error: unknown;
}) {
  if (isLoading) {
    return (
      <section className="rounded-3xl border border-[var(--color-border)] bg-background p-6">
        <Skeleton className="h-6 w-48" />
        <div className="mt-4 grid gap-3 md:grid-cols-4">
          {Array.from({ length: 4 }).map((_, index) => (
            <Skeleton key={index} className="h-20 rounded-2xl" />
          ))}
        </div>
      </section>
    );
  }

  if (error || !progress) {
    return (
      <section className="rounded-3xl border border-[var(--color-border)] bg-background p-6">
        <h2 className="text-xl font-semibold text-[var(--color-foreground)]">Progress summary</h2>
        <p className="mt-2 text-sm text-[var(--color-muted-foreground)]">
          Course progress is unavailable. The course structure can still be reviewed without learner-level data.
        </p>
      </section>
    );
  }

  const topBottlenecks = [...progress.lessons]
    .sort((a, b) => {
      const aRate = a.startedLearnerCount > 0 ? a.startToCompletionRatePercent : a.completionRatePercent;
      const bRate = b.startedLearnerCount > 0 ? b.startToCompletionRatePercent : b.completionRatePercent;
      return aRate - bRate || a.title.localeCompare(b.title);
    })
    .slice(0, 3);
  const topModuleBottlenecks = [...progress.modules]
    .sort((a, b) => {
      const aRate = a.startedLearnerLessons > 0 ? a.startToCompletionRatePercent : a.completionRatePercent;
      const bRate = b.startedLearnerLessons > 0 ? b.startToCompletionRatePercent : b.completionRatePercent;
      return aRate - bRate || a.sortOrder - b.sortOrder || a.title.localeCompare(b.title);
    })
    .slice(0, 3);

  return (
    <section className="rounded-3xl border border-[var(--color-border)] bg-background p-6">
      <div className="flex flex-col gap-2 md:flex-row md:items-start md:justify-between">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[var(--color-muted-foreground)]">Progress</p>
          <h2 className="mt-2 text-2xl font-semibold text-[var(--color-foreground)]">Learner progress summary</h2>
          <p className="mt-2 max-w-2xl text-sm leading-6 text-[var(--color-muted-foreground)]">
            Aggregate-only learner movement from the canonical course progress read model. No contact identity or learner answers are exposed here.
          </p>
        </div>
        <span className="rounded-full border border-[var(--color-border)] px-3 py-1 text-xs font-medium text-[var(--color-foreground)]">
          {progress.progressVersion}
        </span>
      </div>

      <dl className="mt-6 grid gap-3 text-sm md:grid-cols-5">
        <div className="rounded-2xl bg-[var(--color-surface)] p-4">
          <dt className="text-[11px] uppercase tracking-[0.14em] text-[var(--color-muted-foreground)]">Enrollments</dt>
          <dd className="mt-1 text-xl font-semibold text-[var(--color-foreground)]">{progress.counts.enrollments}</dd>
        </div>
        <div className="rounded-2xl bg-[var(--color-surface)] p-4">
          <dt className="text-[11px] uppercase tracking-[0.14em] text-[var(--color-muted-foreground)]">Active</dt>
          <dd className="mt-1 text-xl font-semibold text-[var(--color-foreground)]">{progress.counts.active}</dd>
        </div>
        <div className="rounded-2xl bg-[var(--color-surface)] p-4">
          <dt className="text-[11px] uppercase tracking-[0.14em] text-[var(--color-muted-foreground)]">Completed</dt>
          <dd className="mt-1 text-xl font-semibold text-[var(--color-foreground)]">{progress.counts.completed}</dd>
          <dd className="mt-1 text-xs text-[var(--color-muted-foreground)]">
            {progress.courseActivity.courseCompletionEvents} course events
          </dd>
        </div>
        <div className="rounded-2xl bg-[var(--color-surface)] p-4">
          <dt className="text-[11px] uppercase tracking-[0.14em] text-[var(--color-muted-foreground)]">Dropped</dt>
          <dd className="mt-1 text-xl font-semibold text-[var(--color-foreground)]">{progress.counts.dropped}</dd>
        </div>
      </dl>

      {progress.counts.enrollments === 0 ? (
        <div className="mt-5 rounded-2xl border border-dashed border-[var(--color-border)] bg-[var(--color-surface)] p-4 text-sm text-[var(--color-muted-foreground)]">
          No learner progress has been recorded for this course yet.
        </div>
      ) : (
        <div className="mt-5 grid gap-4 xl:grid-cols-3">
          <div className="rounded-2xl border border-[var(--color-border)] bg-[var(--color-surface)] p-4">
            <h3 className="text-sm font-semibold text-[var(--color-foreground)]">Progress buckets</h3>
            <div className="mt-4 grid gap-3">
              {progress.progressBuckets.map((bucket) => {
                const percent = progress.counts.enrollments > 0
                  ? Math.round((bucket.count / progress.counts.enrollments) * 100)
                  : 0;
                return (
                  <div key={bucket.label}>
                    <div className="flex items-center justify-between text-xs text-[var(--color-muted-foreground)]">
                      <span>{bucket.label}%</span>
                      <span>{bucket.count} learners</span>
                    </div>
                    <div className="mt-1 h-2 overflow-hidden rounded-full bg-background">
                      <div className="h-full rounded-full bg-[var(--color-primary)]" style={{ width: `${percent}%` }} />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          <div className="rounded-2xl border border-[var(--color-border)] bg-[var(--color-surface)] p-4">
            <h3 className="text-sm font-semibold text-[var(--color-foreground)]">Module bottlenecks</h3>
            <div className="mt-4 space-y-3">
              {topModuleBottlenecks.length === 0 ? (
                <p className="text-sm text-[var(--color-muted-foreground)]">No module progress rows yet.</p>
              ) : topModuleBottlenecks.map((module) => (
                <div key={module.id} className="rounded-xl bg-background p-3">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <p className="text-sm font-semibold text-[var(--color-foreground)]">{module.title}</p>
                      <p className="mt-1 text-xs text-[var(--color-muted-foreground)]">
                        {module.lessonCount} lessons · {module.startedLearnerLessons} learner-lessons started
                      </p>
                    </div>
                    <span className="text-sm font-semibold text-[var(--color-foreground)]">
                      {module.startedLearnerLessons > 0 ? module.startToCompletionRatePercent : module.completionRatePercent}%
                    </span>
                  </div>
                  <p className="mt-2 text-xs text-[var(--color-muted-foreground)]">
                    {module.completionCount} lesson completions
                    {module.quizAttempts > 0 ? ` · ${module.quizAttempts} quiz attempts` : ""}
                    {module.exerciseSubmissions > 0 ? ` · ${module.exerciseSubmissions} exercise submissions` : ""}
                  </p>
                </div>
              ))}
            </div>
          </div>

          <div className="rounded-2xl border border-[var(--color-border)] bg-[var(--color-surface)] p-4">
            <h3 className="text-sm font-semibold text-[var(--color-foreground)]">Lesson bottlenecks</h3>
            <div className="mt-4 space-y-3">
              {topBottlenecks.length === 0 ? (
                <p className="text-sm text-[var(--color-muted-foreground)]">No lesson progress rows yet.</p>
              ) : topBottlenecks.map((lesson) => (
                <div key={lesson.id} className="rounded-xl bg-background p-3">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <p className="text-sm font-semibold text-[var(--color-foreground)]">{lesson.title}</p>
                      <p className="mt-1 text-xs text-[var(--color-muted-foreground)]">
                        {lesson.moduleTitle} · {lesson.lessonType}
                      </p>
                    </div>
                    <span className="text-sm font-semibold text-[var(--color-foreground)]">
                      {lesson.startedLearnerCount > 0 ? lesson.startToCompletionRatePercent : lesson.completionRatePercent}%
                    </span>
                  </div>
                  <p className="mt-2 text-xs text-[var(--color-muted-foreground)]">
                    {lesson.startedLearnerCount > 0 ? `${lesson.startedLearnerCount} started · ` : ""}
                    {lesson.completionCount} completions
                    {lesson.quizAttempts > 0 ? ` · ${lesson.quizPassRatePercent}% quiz pass rate across ${lesson.quizAttempts} attempts` : ""}
                    {lesson.exerciseSubmissions > 0 ? ` · ${lesson.exerciseSubmissions} exercise submissions` : ""}
                  </p>
                  {lesson.startedCount > lesson.startedLearnerCount ? (
                    <p className="mt-1 text-[11px] text-[var(--color-muted-foreground)]">
                      {lesson.startedCount} total start events recorded
                    </p>
                  ) : null}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </section>
  );
}

function LearnerOperationsPanel({
  operations,
  isLoading,
  error,
  enabled,
}: {
  operations: CourseLearnerOperationsResponse | null | undefined;
  isLoading: boolean;
  error: unknown;
  enabled: boolean;
}) {
  if (!enabled) return null;

  if (isLoading) {
    return (
      <section className="rounded-3xl border border-[var(--color-border)] bg-background p-6">
        <Skeleton className="h-6 w-72" />
        <div className="mt-4 grid gap-3 md:grid-cols-4">
          {Array.from({ length: 4 }).map((_, index) => (
            <Skeleton key={index} className="h-20 rounded-2xl" />
          ))}
        </div>
      </section>
    );
  }

  if (error || !operations) {
    return (
      <section className="rounded-3xl border border-[var(--color-border)] bg-background p-6">
        <h2 className="text-xl font-semibold text-[var(--color-foreground)]">Learner operations</h2>
        <p className="mt-2 text-sm text-[var(--color-muted-foreground)]">
          Learner operations evidence is unavailable. Keep roster, assignment, and progress writes blocked.
        </p>
      </section>
    );
  }

  const ready = operations.decision === "READY_FOR_LEARNER_OPERATIONS_REVIEW";
  const blockingChecks = operations.checks.filter((check) => check.severity === "blocker" && !check.ok);

  return (
    <section className="rounded-3xl border border-[var(--color-border)] bg-background p-6">
      <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[var(--color-muted-foreground)]">Learner operations</p>
          <h2 className="mt-2 text-2xl font-semibold text-[var(--color-foreground)]">
            {ready ? "Roster evidence is internally consistent" : "Roster evidence needs attention"}
          </h2>
          <p className="mt-2 max-w-2xl text-sm leading-6 text-[var(--color-muted-foreground)]">
            Staff-only anonymized cohort health for the reusable Stero LMS spine. Learner identity, assignment writes, enrollment writes, progress writes, and certificate issuance stay blocked.
          </p>
        </div>
        <span className={`rounded-full border px-3 py-1 text-xs font-semibold ${
          ready
            ? "border-[var(--color-success)]/30 text-[var(--color-success)]"
            : "border-[var(--color-warning)]/30 text-[var(--color-warning)]"
        }`}
        >
          {operations.decision}
        </span>
      </div>

      <dl className="mt-6 grid gap-3 text-sm md:grid-cols-4 xl:grid-cols-6">
        <div className="rounded-2xl bg-[var(--color-surface)] p-4">
          <dt className="text-[11px] uppercase tracking-[0.14em] text-[var(--color-muted-foreground)]">Enrollments</dt>
          <dd className="mt-1 text-xl font-semibold text-[var(--color-foreground)]">{operations.counts.enrollments}</dd>
          <dd className="mt-1 text-xs text-[var(--color-muted-foreground)]">{operations.counts.recentEnrollments30d} in 30d</dd>
        </div>
        <div className="rounded-2xl bg-[var(--color-surface)] p-4">
          <dt className="text-[11px] uppercase tracking-[0.14em] text-[var(--color-muted-foreground)]">Active no activity</dt>
          <dd className="mt-1 text-xl font-semibold text-[var(--color-foreground)]">{operations.counts.activeWithoutActivity}</dd>
          <dd className="mt-1 text-xs text-[var(--color-muted-foreground)]">{operations.counts.activeWithActivity} active with starts</dd>
        </div>
        <div className="rounded-2xl bg-[var(--color-surface)] p-4">
          <dt className="text-[11px] uppercase tracking-[0.14em] text-[var(--color-muted-foreground)]">In progress</dt>
          <dd className="mt-1 text-xl font-semibold text-[var(--color-foreground)]">{operations.counts.activeInProgress}</dd>
          <dd className="mt-1 text-xs text-[var(--color-muted-foreground)]">{operations.counts.activeNearCompletion} near completion</dd>
        </div>
        <div className="rounded-2xl bg-[var(--color-surface)] p-4">
          <dt className="text-[11px] uppercase tracking-[0.14em] text-[var(--color-muted-foreground)]">Lesson starts</dt>
          <dd className="mt-1 text-xl font-semibold text-[var(--color-foreground)]">{operations.activity.lessonStartedLearners}</dd>
          <dd className="mt-1 text-xs text-[var(--color-muted-foreground)]">{operations.activity.lessonStartedEvents} events</dd>
        </div>
        <div className="rounded-2xl bg-[var(--color-surface)] p-4">
          <dt className="text-[11px] uppercase tracking-[0.14em] text-[var(--color-muted-foreground)]">Exercises</dt>
          <dd className="mt-1 text-xl font-semibold text-[var(--color-foreground)]">{operations.activity.exerciseSubmissions}</dd>
          <dd className="mt-1 text-xs text-[var(--color-muted-foreground)]">submissions observed</dd>
        </div>
        <div className="rounded-2xl bg-[var(--color-surface)] p-4">
          <dt className="text-[11px] uppercase tracking-[0.14em] text-[var(--color-muted-foreground)]">Needs nudge</dt>
          <dd className="mt-1 text-xl font-semibold text-[var(--color-foreground)]">{operations.riskSegments.needsNudge}</dd>
          <dd className="mt-1 text-xs text-[var(--color-muted-foreground)]">{operations.riskSegments.readyForCompletionNudge} near completion</dd>
        </div>
        <div className="rounded-2xl bg-[var(--color-surface)] p-4">
          <dt className="text-[11px] uppercase tracking-[0.14em] text-[var(--color-muted-foreground)]">Identity visible</dt>
          <dd className="mt-1 text-xl font-semibold text-[var(--color-foreground)]">{operations.capabilities.learnerIdentityVisible ? "Yes" : "No"}</dd>
          <dd className="mt-1 text-xs text-[var(--color-muted-foreground)]">staff aggregate only</dd>
        </div>
      </dl>

      <div className="mt-5 grid gap-4 lg:grid-cols-[1fr_1fr]">
        <div className={`rounded-2xl border p-4 ${
          blockingChecks.length === 0
            ? "border-[var(--color-success)]/30 bg-[var(--color-success)]/5"
            : "border-[var(--color-warning)]/30 bg-[var(--color-warning)]/5"
        }`}
        >
          <h3 className="text-sm font-semibold text-[var(--color-foreground)]">
            {blockingChecks.length === 0 ? "Roster consistency checks passed" : "Roster consistency blockers"}
          </h3>
          {blockingChecks.length === 0 ? (
            <p className="mt-2 text-sm text-[var(--color-muted-foreground)]">
              Enrollment status, progress bounds, and completion fields are ready for shadow operations review.
            </p>
          ) : (
            <ul className="mt-3 space-y-2 text-sm text-[var(--color-muted-foreground)]">
              {blockingChecks.map((check) => (
                <li key={check.key}>{check.summary}</li>
              ))}
            </ul>
          )}
        </div>

        <div className="rounded-2xl border border-[var(--color-border)] bg-[var(--color-surface)] p-4">
          <h3 className="text-sm font-semibold text-[var(--color-foreground)]">Cohort triage segments</h3>
          <dl className="mt-3 grid gap-3 text-sm sm:grid-cols-2">
            <div className="rounded-xl bg-background p-3">
              <dt className="text-[11px] uppercase tracking-[0.12em] text-[var(--color-muted-foreground)]">Not started after 7d</dt>
              <dd className="mt-1 font-semibold text-[var(--color-foreground)]">{operations.riskSegments.atRiskNotStarted7d}</dd>
            </div>
            <div className="rounded-xl bg-background p-3">
              <dt className="text-[11px] uppercase tracking-[0.12em] text-[var(--color-muted-foreground)]">Stalled after 14d</dt>
              <dd className="mt-1 font-semibold text-[var(--color-foreground)]">{operations.riskSegments.atRiskStalled14d}</dd>
            </div>
            <div className="rounded-xl bg-background p-3">
              <dt className="text-[11px] uppercase tracking-[0.12em] text-[var(--color-muted-foreground)]">Needs nudge</dt>
              <dd className="mt-1 font-semibold text-[var(--color-foreground)]">{operations.riskSegments.needsNudge}</dd>
            </div>
            <div className="rounded-xl bg-background p-3">
              <dt className="text-[11px] uppercase tracking-[0.12em] text-[var(--color-muted-foreground)]">Completion nudge</dt>
              <dd className="mt-1 font-semibold text-[var(--color-foreground)]">{operations.riskSegments.readyForCompletionNudge}</dd>
            </div>
          </dl>
        </div>

        <div className="rounded-2xl border border-[var(--color-border)] bg-[var(--color-surface)] p-4">
          <h3 className="text-sm font-semibold text-[var(--color-foreground)]">Still blocked</h3>
          <ul className="mt-3 space-y-2 text-sm text-[var(--color-muted-foreground)]">
            <li>Assignment writes: {operations.capabilities.assignmentWriteEnabled ? "Mounted" : "Blocked"}</li>
            <li>Enrollment writes: {operations.capabilities.enrollmentWriteEnabled ? "Mounted" : "Blocked"}</li>
            <li>Progress writes: {operations.capabilities.progressWriteEnabled ? "Mounted" : "Blocked"}</li>
            <li>English Ease primary: {operations.capabilities.englishEasePrimaryMode ? "Yes" : "No"}</li>
          </ul>
        </div>
      </div>
    </section>
  );
}

function CapabilityMatrixPanel({
  matrix,
  isLoading,
  error,
  enabled,
}: {
  matrix: CourseCapabilityMatrixResponse | null | undefined;
  isLoading: boolean;
  error: unknown;
  enabled: boolean;
}) {
  if (!enabled) return null;

  if (isLoading) {
    return (
      <section className="rounded-3xl border border-[var(--color-border)] bg-background p-6">
        <Skeleton className="h-6 w-72" />
        <div className="mt-4 grid gap-3 md:grid-cols-4">
          {Array.from({ length: 4 }).map((_, index) => (
            <Skeleton key={index} className="h-20 rounded-2xl" />
          ))}
        </div>
      </section>
    );
  }

  if (error || !matrix) {
    return (
      <section className="rounded-3xl border border-[var(--color-border)] bg-background p-6">
        <h2 className="text-xl font-semibold text-[var(--color-foreground)]">LMS capability matrix</h2>
        <p className="mt-2 text-sm text-[var(--color-muted-foreground)]">
          Capability evidence is unavailable. Keep Stero in shadow mode.
        </p>
      </section>
    );
  }

  const stateLabel = (state: CourseCapabilityMatrixResponse["capabilities"][number]["state"]) => formatSnakeCaseLabel(state, "unknown");
  const stateClass = (state: CourseCapabilityMatrixResponse["capabilities"][number]["state"]) => {
    if (state === "blocked") return "border-[var(--color-warning)]/30 text-[var(--color-warning)]";
    if (state === "observed") return "border-[var(--color-success)]/30 text-[var(--color-success)]";
    return "border-[var(--color-border)] text-[var(--color-foreground)]";
  };

  return (
    <section className="rounded-3xl border border-[var(--color-border)] bg-background p-6">
      <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[var(--color-muted-foreground)]">LMS capability matrix</p>
          <h2 className="mt-2 text-2xl font-semibold text-[var(--color-foreground)]">
            {matrix.decision === "READY_FOR_LMS_CAPABILITY_REVIEW" ? "Reusable Stero LMS capabilities mapped" : "Capability evidence incomplete"}
          </h2>
          <p className="mt-2 max-w-2xl text-sm leading-6 text-[var(--color-muted-foreground)]">
            Tenant-neutral view of LMS reads, gated writes, observed behavior, and blocked actions. This does not approve public serving or primary cutover.
          </p>
        </div>
        <span className="rounded-full border border-[var(--color-border)] px-3 py-1 text-xs font-semibold text-[var(--color-foreground)]">
          {matrix.recommendedMode}
        </span>
      </div>

      <dl className="mt-6 grid gap-3 text-sm md:grid-cols-4">
        <div className="rounded-2xl bg-[var(--color-surface)] p-4">
          <dt className="text-[11px] uppercase tracking-[0.14em] text-[var(--color-muted-foreground)]">Ready reads</dt>
          <dd className="mt-1 text-xl font-semibold text-[var(--color-foreground)]">{matrix.summary.readyRead}</dd>
        </div>
        <div className="rounded-2xl bg-[var(--color-surface)] p-4">
          <dt className="text-[11px] uppercase tracking-[0.14em] text-[var(--color-muted-foreground)]">Contracts</dt>
          <dd className="mt-1 text-xl font-semibold text-[var(--color-foreground)]">{matrix.summary.contractMounted}</dd>
        </div>
        <div className="rounded-2xl bg-[var(--color-surface)] p-4">
          <dt className="text-[11px] uppercase tracking-[0.14em] text-[var(--color-muted-foreground)]">Observed</dt>
          <dd className="mt-1 text-xl font-semibold text-[var(--color-foreground)]">{matrix.summary.observed}</dd>
        </div>
        <div className="rounded-2xl bg-[var(--color-surface)] p-4">
          <dt className="text-[11px] uppercase tracking-[0.14em] text-[var(--color-muted-foreground)]">Blocked</dt>
          <dd className="mt-1 text-xl font-semibold text-[var(--color-foreground)]">{matrix.summary.blocked}</dd>
        </div>
      </dl>

      <div className="mt-5 grid gap-3 md:grid-cols-2 xl:grid-cols-3">
        {matrix.capabilities.map((item) => (
          <div key={item.key} className="rounded-2xl border border-[var(--color-border)] bg-[var(--color-surface)] p-4">
            <div className="flex items-start justify-between gap-3">
              <p className="text-sm font-semibold text-[var(--color-foreground)]">{item.label}</p>
              <span className={`rounded-full border px-2 py-1 text-[11px] font-semibold ${stateClass(item.state)}`}>
                {stateLabel(item.state)}
              </span>
            </div>
            {item.blockedActions.length > 0 ? (
              <p className="mt-3 text-xs text-[var(--color-muted-foreground)]">
                Blocks: {item.blockedActions.slice(0, 3).join(", ")}
                {item.blockedActions.length > 3 ? "..." : ""}
              </p>
            ) : null}
          </div>
        ))}
      </div>
    </section>
  );
}

function ReadinessPanel({
  readiness,
  isLoading,
  error,
}: {
  readiness: CourseReadinessResponse | null | undefined;
  isLoading: boolean;
  error: unknown;
}) {
  if (isLoading) {
    return (
      <section className="rounded-3xl border border-[var(--color-border)] bg-background p-6">
        <Skeleton className="h-6 w-56" />
        <div className="mt-4 grid gap-3 md:grid-cols-3">
          {Array.from({ length: 3 }).map((_, index) => (
            <Skeleton key={index} className="h-24 rounded-2xl" />
          ))}
        </div>
      </section>
    );
  }

  if (error || !readiness) {
    return (
      <section className="rounded-3xl border border-[var(--color-border)] bg-background p-6">
        <h2 className="text-xl font-semibold text-[var(--color-foreground)]">Internal readiness</h2>
        <p className="mt-2 text-sm text-[var(--color-muted-foreground)]">
          Course readiness is unavailable. Keep this course in manual review until the API readiness surface responds.
        </p>
      </section>
    );
  }

  const blockingChecks = readiness.checks.filter((check) => check.severity === "blocker" && !check.ok);
  const noticeChecks = readiness.checks.filter((check) => check.severity === "notice");

  return (
    <section className="rounded-3xl border border-[var(--color-border)] bg-background p-6">
      <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[var(--color-muted-foreground)]">Internal readiness</p>
          <h2 className="mt-2 text-2xl font-semibold text-[var(--color-foreground)]">
            {readiness.decision === "READY_FOR_INTERNAL_STAFF_REVIEW" ? "Ready for staff shadow review" : "Structure incomplete"}
          </h2>
          <p className="mt-2 max-w-2xl text-sm leading-6 text-[var(--color-muted-foreground)]">
            This is a read-only Stero readiness view for staff/cohort review. It does not approve learner serving, target writes, parity, or cutover.
          </p>
        </div>
        <span className={`rounded-full border px-3 py-1 text-xs font-semibold ${
          readiness.decision === "READY_FOR_INTERNAL_STAFF_REVIEW"
            ? "border-[var(--color-success)]/30 text-[var(--color-success)]"
            : "border-[var(--color-warning)]/30 text-[var(--color-warning)]"
        }`}
        >
          {readiness.recommendedMode}
        </span>
      </div>

      <dl className="mt-6 grid gap-3 text-sm md:grid-cols-6">
        <div className="rounded-2xl bg-[var(--color-surface)] p-4">
          <dt className="text-[11px] uppercase tracking-[0.14em] text-[var(--color-muted-foreground)]">Serving allowed</dt>
          <dd className="mt-1 text-lg font-semibold text-[var(--color-foreground)]">{readiness.servingAllowed ? "Yes" : "No"}</dd>
        </div>
        <div className="rounded-2xl bg-[var(--color-surface)] p-4">
          <dt className="text-[11px] uppercase tracking-[0.14em] text-[var(--color-muted-foreground)]">Blockers</dt>
          <dd className="mt-1 text-lg font-semibold text-[var(--color-foreground)]">{readiness.blockers.length}</dd>
        </div>
        <div className="rounded-2xl bg-[var(--color-surface)] p-4">
          <dt className="text-[11px] uppercase tracking-[0.14em] text-[var(--color-muted-foreground)]">Linked pages</dt>
          <dd className="mt-1 text-lg font-semibold text-[var(--color-foreground)]">{readiness.counts.linkedPages}/{readiness.counts.lessons}</dd>
        </div>
        <div className="rounded-2xl bg-[var(--color-surface)] p-4">
          <dt className="text-[11px] uppercase tracking-[0.14em] text-[var(--color-muted-foreground)]">Video assets</dt>
          <dd className="mt-1 text-lg font-semibold text-[var(--color-foreground)]">{readiness.counts.externalVideoLessons}/{readiness.counts.videoLessons}</dd>
        </div>
      </dl>

      {blockingChecks.length > 0 ? (
        <div className="mt-5 rounded-2xl border border-[var(--color-warning)]/30 bg-[var(--color-warning)]/5 p-4">
          <h3 className="text-sm font-semibold text-[var(--color-foreground)]">Blocking checks</h3>
          <ul className="mt-3 space-y-2 text-sm text-[var(--color-muted-foreground)]">
            {blockingChecks.map((check) => (
              <li key={check.key}>{check.summary}</li>
            ))}
          </ul>
        </div>
      ) : (
        <div className="mt-5 rounded-2xl border border-[var(--color-success)]/30 bg-[var(--color-success)]/5 p-4 text-sm text-[var(--color-foreground)]">
          Course structure, required lesson assets, and linked content references are complete enough for internal staff shadow review.
        </div>
      )}

      {noticeChecks.length > 0 ? (
        <div className="mt-4 grid gap-3 md:grid-cols-2">
          {noticeChecks.map((check) => (
            <div key={check.key} className="rounded-2xl border border-[var(--color-border)] bg-[var(--color-surface)] p-3 text-sm text-[var(--color-muted-foreground)]">
              {check.summary}
            </div>
          ))}
        </div>
      ) : null}
    </section>
  );
}

function CertificateReadinessPanel({
  readiness,
  isLoading,
  error,
  enabled,
}: {
  readiness: CourseCertificateReadinessResponse | null | undefined;
  isLoading: boolean;
  error: unknown;
  enabled: boolean;
}) {
  if (!enabled) return null;

  if (isLoading) {
    return (
      <section className="rounded-3xl border border-[var(--color-border)] bg-background p-6">
        <Skeleton className="h-6 w-72" />
        <div className="mt-4 grid gap-3 md:grid-cols-4">
          {Array.from({ length: 4 }).map((_, index) => (
            <Skeleton key={index} className="h-20 rounded-2xl" />
          ))}
        </div>
      </section>
    );
  }

  if (error || !readiness) {
    return (
      <section className="rounded-3xl border border-[var(--color-border)] bg-background p-6">
        <h2 className="text-xl font-semibold text-[var(--color-foreground)]">Certificate readiness</h2>
        <p className="mt-2 text-sm text-[var(--color-muted-foreground)]">
          Certificate evidence is unavailable. Certificate issuance and public verification remain blocked.
        </p>
      </section>
    );
  }

  const ready = readiness.decision === "READY_FOR_CERTIFICATE_READINESS_REVIEW";

  return (
    <section className="rounded-3xl border border-[var(--color-border)] bg-background p-6">
      <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[var(--color-muted-foreground)]">Certificate readiness</p>
          <h2 className="mt-2 text-2xl font-semibold text-[var(--color-foreground)]">
            {ready ? "Certificate records are internally consistent" : "Certificate records need attention"}
          </h2>
          <p className="mt-2 max-w-2xl text-sm leading-6 text-[var(--color-muted-foreground)]">
            Staff-only certificate evidence for the reusable Stero LMS spine. Token values, issuance, rendering, and public verification stay blocked.
          </p>
        </div>
        <span className={`rounded-full border px-3 py-1 text-xs font-semibold ${
          ready
            ? "border-[var(--color-success)]/30 text-[var(--color-success)]"
            : "border-[var(--color-warning)]/30 text-[var(--color-warning)]"
        }`}
        >
          {readiness.decision}
        </span>
      </div>
      <dl className="mt-6 grid gap-3 text-sm md:grid-cols-5">
        <div className="rounded-2xl bg-[var(--color-surface)] p-4">
          <dt className="text-[11px] uppercase tracking-[0.14em] text-[var(--color-muted-foreground)]">Completed</dt>
          <dd className="mt-1 text-xl font-semibold text-[var(--color-foreground)]">{readiness.counts.completedEnrollments}</dd>
        </div>
        <div className="rounded-2xl bg-[var(--color-surface)] p-4">
          <dt className="text-[11px] uppercase tracking-[0.14em] text-[var(--color-muted-foreground)]">Token presence</dt>
          <dd className="mt-1 text-xl font-semibold text-[var(--color-foreground)]">{readiness.counts.certificateTokensPresent}</dd>
        </div>
        <div className="rounded-2xl bg-[var(--color-surface)] p-4">
          <dt className="text-[11px] uppercase tracking-[0.14em] text-[var(--color-muted-foreground)]">Generated events</dt>
          <dd className="mt-1 text-xl font-semibold text-[var(--color-foreground)]">{readiness.counts.certificateGeneratedEvents}</dd>
        </div>
        <div className="rounded-2xl bg-[var(--color-surface)] p-4">
          <dt className="text-[11px] uppercase tracking-[0.14em] text-[var(--color-muted-foreground)]">Issue</dt>
          <dd className="mt-1 text-xl font-semibold text-[var(--color-foreground)]">{readiness.capabilities.certificateIssueEnabled ? "Mounted" : "Blocked"}</dd>
        </div>
        <div className="rounded-2xl bg-[var(--color-surface)] p-4">
          <dt className="text-[11px] uppercase tracking-[0.14em] text-[var(--color-muted-foreground)]">Public verify</dt>
          <dd className="mt-1 text-xl font-semibold text-[var(--color-foreground)]">{readiness.capabilities.publicVerificationEnabled ? "Mounted" : "Blocked"}</dd>
        </div>
      </dl>
      <div className="mt-5 grid gap-3 md:grid-cols-3">
        {readiness.checks.map((check) => (
          <div key={check.key} className="rounded-2xl border border-[var(--color-border)] bg-[var(--color-surface)] p-4">
            <p className={`text-sm font-semibold ${check.ok ? "text-[var(--color-success)]" : "text-[var(--color-warning)]"}`}>
              {check.ok ? "OK" : "Needs attention"}
            </p>
            <p className="mt-2 text-sm text-[var(--color-muted-foreground)]">{check.summary}</p>
          </div>
        ))}
      </div>
    </section>
  );
}

function StaffReviewPacketPanel({
  packet,
  isLoading,
  error,
}: {
  packet: CourseReviewPacketResponse | null | undefined;
  isLoading: boolean;
  error: unknown;
}) {
  if (isLoading) {
    return (
      <section className="rounded-3xl border border-[var(--color-border)] bg-background p-6">
        <Skeleton className="h-6 w-60" />
        <div className="mt-4 grid gap-3 md:grid-cols-4">
          {Array.from({ length: 4 }).map((_, index) => (
            <Skeleton key={index} className="h-24 rounded-2xl" />
          ))}
        </div>
      </section>
    );
  }

  if (error || !packet) {
    return (
      <section className="rounded-3xl border border-[var(--color-border)] bg-background p-6">
        <h2 className="text-xl font-semibold text-[var(--color-foreground)]">Staff review packet</h2>
        <p className="mt-2 text-sm text-[var(--color-muted-foreground)]">
          Page review evidence is unavailable. Keep this course in internal review until the packet surface responds.
        </p>
      </section>
    );
  }

  const orderedLessons = [...packet.lessons]
    .sort((a, b) => a.module.sortOrder - b.module.sortOrder || a.sortOrder - b.sortOrder || a.title.localeCompare(b.title));

  return (
    <section className="rounded-3xl border border-[var(--color-border)] bg-background p-6">
      <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[var(--color-muted-foreground)]">Staff review packet</p>
          <h2 className="mt-2 text-2xl font-semibold text-[var(--color-foreground)]">
            {packet.decision === "READY_FOR_STAFF_PAGE_REVIEW" ? "Ready for staff page review" : "Page review incomplete"}
          </h2>
          <p className="mt-2 max-w-2xl text-sm leading-6 text-[var(--color-muted-foreground)]">
            Current-version page review status for course lessons. This packet is read-only and does not approve learner serving, target writes, parity, or cutover.
          </p>
        </div>
        <span className={`rounded-full border px-3 py-1 text-xs font-semibold ${
          packet.decision === "READY_FOR_STAFF_PAGE_REVIEW"
            ? "border-[var(--color-success)]/30 text-[var(--color-success)]"
            : "border-[var(--color-warning)]/30 text-[var(--color-warning)]"
        }`}
        >
          {packet.recommendedMode}
        </span>
      </div>

      <dl className="mt-6 grid gap-3 text-sm md:grid-cols-4">
        <div className="rounded-2xl bg-[var(--color-surface)] p-4">
          <dt className="text-[11px] uppercase tracking-[0.14em] text-[var(--color-muted-foreground)]">Serving allowed</dt>
          <dd className="mt-1 text-lg font-semibold text-[var(--color-foreground)]">{packet.servingAllowed ? "Yes" : "No"}</dd>
        </div>
        <div className="rounded-2xl bg-[var(--color-surface)] p-4">
          <dt className="text-[11px] uppercase tracking-[0.14em] text-[var(--color-muted-foreground)]">Approved current reviews</dt>
          <dd className="mt-1 text-lg font-semibold text-[var(--color-foreground)]">{packet.counts.approvedCurrentReviews}/{packet.counts.lessonsWithPages}</dd>
        </div>
        <div className="rounded-2xl bg-[var(--color-surface)] p-4">
          <dt className="text-[11px] uppercase tracking-[0.14em] text-[var(--color-muted-foreground)]">Pending or missing</dt>
          <dd className="mt-1 text-lg font-semibold text-[var(--color-foreground)]">
            {packet.counts.pendingCurrentReviews + packet.counts.missingCurrentReviews}
          </dd>
        </div>
        <div className="rounded-2xl bg-[var(--color-surface)] p-4">
          <dt className="text-[11px] uppercase tracking-[0.14em] text-[var(--color-muted-foreground)]">Changes requested</dt>
          <dd className="mt-1 text-lg font-semibold text-[var(--color-foreground)]">{packet.counts.changesRequested}</dd>
        </div>
      </dl>

      {packet.blockers.length > 0 ? (
        <div className="mt-5 rounded-2xl border border-[var(--color-warning)]/30 bg-[var(--color-warning)]/5 p-4">
          <h3 className="text-sm font-semibold text-[var(--color-foreground)]">Review blockers</h3>
          <ul className="mt-3 space-y-2 text-sm text-[var(--color-muted-foreground)]">
            {packet.blockers.map((blocker) => (
              <li key={blocker}>{blocker}</li>
            ))}
          </ul>
        </div>
      ) : (
        <div className="mt-5 rounded-2xl border border-[var(--color-success)]/30 bg-[var(--color-success)]/5 p-4 text-sm text-[var(--color-foreground)]">
          All linked lesson pages have an approved review for the current page version.
        </div>
      )}

      <div className="mt-5 divide-y divide-[var(--color-border)] rounded-2xl border border-[var(--color-border)] bg-[var(--color-surface)]">
        {orderedLessons.map((lesson) => {
          const href = buildStaffReviewHref(packet.course.id, lesson);
          return (
            <div key={lesson.id} className="flex flex-col gap-3 p-4 md:flex-row md:items-center md:justify-between">
              <div>
                <p className="text-sm font-semibold text-[var(--color-foreground)]">{lesson.title}</p>
                <p className="mt-1 text-xs text-[var(--color-muted-foreground)]">
                  {lesson.module.title} · {lesson.lessonType}
                  {lesson.page?.title ? ` · ${lesson.page.title}` : ""}
                  {lesson.page?.currentVersionNumber !== null && lesson.page?.currentVersionNumber !== undefined
                    ? ` · v${lesson.page.currentVersionNumber}`
                    : ""}
                </p>
                {lesson.blockers.length > 0 ? (
                  <p className="mt-2 text-xs text-[var(--color-warning)]">{lesson.blockers.join(", ")}</p>
                ) : null}
              </div>
              <div className="flex flex-wrap items-center gap-2 text-xs">
                <span className="rounded-full bg-background px-2 py-1 font-semibold capitalize text-[var(--color-foreground)]">
                  {formatReviewState(lesson.reviewState)}
                </span>
                {href ? (
                  <Link
                    to={href}
                    className="rounded-full border border-[var(--color-border)] bg-background px-3 py-1.5 font-semibold text-[var(--color-foreground)] transition hover:border-[var(--color-primary)]"
                  >
                    Open review
                  </Link>
                ) : null}
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
}

function VideoDeliveryReviewPanel({
  manifest,
  isLoading,
  error,
}: {
  manifest: CourseVideoDeliveryResponse | null | undefined;
  isLoading: boolean;
  error: unknown;
}) {
  if (isLoading) {
    return (
      <section className="rounded-3xl border border-[var(--color-border)] bg-background p-6">
        <Skeleton className="h-6 w-64" />
        <div className="mt-4 grid gap-3 md:grid-cols-4">
          {Array.from({ length: 4 }).map((_, index) => (
            <Skeleton key={index} className="h-24 rounded-2xl" />
          ))}
        </div>
      </section>
    );
  }

  if (error || !manifest) {
    return (
      <section className="rounded-3xl border border-[var(--color-border)] bg-background p-6">
        <h2 className="text-xl font-semibold text-[var(--color-foreground)]">Video delivery review</h2>
        <p className="mt-2 text-sm text-[var(--color-muted-foreground)]">
          Video delivery evidence is unavailable. Keep video lessons in staff review until the delivery manifest responds.
        </p>
      </section>
    );
  }

  const actionCounts = manifest.videoAssetMountPlan?.requiredActionCounts
    ?? manifest.summary.requiredActionCounts
    ?? {
      none: 0,
      mountVideoAsset: manifest.summary.missingAssets,
      mountSafeEmbedUrl: manifest.summary.invalidAssets,
      manualProviderReview: manifest.summary.manualReviewRequired,
    };
  const actionInstructions = manifest.videoAssetMountPlan?.instructions?.length
    ? manifest.videoAssetMountPlan.instructions
    : [
      "Review mounted lesson media in the course lesson page builder before any learner-serving pilot.",
      "Rerun the video-delivery and release-candidate gates after media evidence is refreshed.",
    ];
  const orderedLessons = [...(manifest.lessons ?? [])]
    .sort((a, b) => a.module.sortOrder - b.module.sortOrder || a.sortOrder - b.sortOrder || a.title.localeCompare(b.title));

  return (
    <section className="rounded-3xl border border-[var(--color-border)] bg-background p-6">
      <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[var(--color-muted-foreground)]">Video delivery review</p>
          <h2 className="mt-2 text-2xl font-semibold text-[var(--color-foreground)]">
            {manifest.decision === "READY_FOR_VIDEO_DELIVERY_REVIEW" ? "Ready for video delivery review" : "Video delivery incomplete"}
          </h2>
          <p className="mt-2 max-w-2xl text-sm leading-6 text-[var(--color-muted-foreground)]">
            Staff-only review of embedded video readiness. This surface is read-only and does not approve learner serving, publishing, or media writes.
          </p>
        </div>
        <span className={`rounded-full border px-3 py-1 text-xs font-semibold ${
          manifest.decision === "READY_FOR_VIDEO_DELIVERY_REVIEW"
            ? "border-[var(--color-success)]/30 text-[var(--color-success)]"
            : "border-[var(--color-warning)]/30 text-[var(--color-warning)]"
        }`}
        >
          {manifest.recommendedMode}
        </span>
      </div>

      <dl className="mt-6 grid gap-3 text-sm md:grid-cols-4">
        <div className="rounded-2xl bg-[var(--color-surface)] p-4">
          <dt className="text-[11px] uppercase tracking-[0.14em] text-[var(--color-muted-foreground)]">Serving allowed</dt>
          <dd className="mt-1 text-lg font-semibold text-[var(--color-foreground)]">{manifest.servingAllowed ? "Yes" : "No"}</dd>
        </div>
        <div className="rounded-2xl bg-[var(--color-surface)] p-4">
          <dt className="text-[11px] uppercase tracking-[0.14em] text-[var(--color-muted-foreground)]">Trusted embeds</dt>
          <dd className="mt-1 text-lg font-semibold text-[var(--color-foreground)]">{manifest.summary.trustedEmbeds}/{manifest.summary.videoLessons}</dd>
        </div>
        <div className="rounded-2xl bg-[var(--color-surface)] p-4">
          <dt className="text-[11px] uppercase tracking-[0.14em] text-[var(--color-muted-foreground)]">Sandboxed review</dt>
          <dd className="mt-1 text-lg font-semibold text-[var(--color-foreground)]">{manifest.summary.sandboxedEmbeds}</dd>
        </div>
        <div className="rounded-2xl bg-[var(--color-surface)] p-4">
          <dt className="text-[11px] uppercase tracking-[0.14em] text-[var(--color-muted-foreground)]">Missing or invalid</dt>
          <dd className="mt-1 text-lg font-semibold text-[var(--color-foreground)]">
            {manifest.summary.missingAssets + manifest.summary.invalidAssets}
          </dd>
        </div>
      </dl>

      {manifest.blockers.length > 0 ? (
        <div className="mt-5 rounded-2xl border border-[var(--color-warning)]/30 bg-[var(--color-warning)]/5 p-4">
          <h3 className="text-sm font-semibold text-[var(--color-foreground)]">Next video migration actions</h3>
          <p className="mt-2 text-sm text-[var(--color-muted-foreground)]">
            {actionCounts.mountSafeEmbedUrl} lessons need safe embed URLs,
            {" "}{actionCounts.mountVideoAsset} need video assets mounted, and
            {" "}{actionCounts.manualProviderReview} need provider review.
          </p>
          <ul className="mt-3 space-y-2 text-sm text-[var(--color-muted-foreground)]">
            {actionInstructions.map((instruction) => (
              <li key={instruction}>{instruction}</li>
            ))}
          </ul>
        </div>
      ) : (
        <div className="mt-5 rounded-2xl border border-[var(--color-success)]/30 bg-[var(--color-success)]/5 p-4 text-sm text-[var(--color-foreground)]">
          Every video lesson has embeddable delivery metadata for staff review.
        </div>
      )}

      <div className="mt-5 grid gap-4 lg:grid-cols-2">
        {orderedLessons.length === 0 ? (
          <div className="rounded-2xl border border-dashed border-[var(--color-border)] bg-[var(--color-surface)] p-4 text-sm text-[var(--color-muted-foreground)]">
            No video lessons are mounted for this course.
          </div>
        ) : orderedLessons.map((lesson) => (
          <article key={lesson.id} className="overflow-hidden rounded-2xl border border-[var(--color-border)] bg-[var(--color-surface)]">
            <div className="flex flex-wrap items-start justify-between gap-3 p-4">
              <div>
                <p className="text-sm font-semibold text-[var(--color-foreground)]">{lesson.title}</p>
                <p className="mt-1 text-xs text-[var(--color-muted-foreground)]">
                  {lesson.module.title}
                  {lesson.estimatedMinutes !== null ? ` · ${lesson.estimatedMinutes} min` : ""}
                  {lesson.page?.title ? ` · ${lesson.page.title}` : ""}
                </p>
              </div>
              <span className="rounded-full bg-background px-2 py-1 text-xs font-semibold capitalize text-[var(--color-foreground)]">
                {formatDeliveryStatus(lesson.delivery.status)}
              </span>
            </div>

            {lesson.delivery.previewAllowed && lesson.delivery.embedUrl ? (
              <div className="aspect-video bg-black">
                <iframe
                  src={lesson.delivery.embedUrl}
                  title={`${lesson.title} video delivery review`}
                  loading="lazy"
                  sandbox={lesson.delivery.isSandboxed ? "allow-scripts allow-same-origin allow-presentation" : undefined}
                  allow="autoplay; encrypted-media; picture-in-picture"
                  allowFullScreen
                  className="h-full w-full border-0"
                />
              </div>
            ) : (
              <div className="m-4 rounded-2xl border border-dashed border-[var(--color-border)] bg-background p-5 text-sm text-[var(--color-muted-foreground)]">
                Video preview is not available until a safe embed URL is mounted.
              </div>
            )}

            <div className="space-y-2 p-4 text-xs text-[var(--color-muted-foreground)]">
              <p>
                Provider: <span className="font-semibold capitalize text-[var(--color-foreground)]">
                  {lesson.delivery.provider || lesson.delivery.sourceProviderHint
                    ? formatVideoProvider(lesson.delivery.provider ?? lesson.delivery.sourceProviderHint ?? "")
                    : "not mounted"}
                </span>
              </p>
              <p>
                Required action: <span className="font-semibold text-[var(--color-foreground)]">
                  {formatVideoRequiredAction(lesson.delivery.requiredAction)}
                </span>
              </p>
              <p>
                Source URL visible: <span className="font-semibold text-[var(--color-foreground)]">{lesson.delivery.sourceVisible ? "Yes" : "No"}</span>
              </p>
              <p>{lesson.delivery.operatorMessage}</p>
              {lesson.delivery.manualReviewRequired ? (
                <p className="text-[var(--color-warning)]">Manual provider review required before any serving decision.</p>
              ) : null}
              {lesson.blockers.length > 0 ? (
                <p className="text-[var(--color-warning)]">{lesson.blockers.join(", ")}</p>
              ) : null}
            </div>
          </article>
        ))}
      </div>
    </section>
  );
}

function ReleaseCandidateReadPacketPanel({
  packet,
  isLoading,
  error,
  enabled,
}: {
  packet: CourseReleaseCandidateReadPacketResponse | null | undefined;
  isLoading: boolean;
  error: unknown;
  enabled: boolean;
}) {
  if (!enabled) return null;

  if (isLoading) {
    return (
      <section className="rounded-3xl border border-[var(--color-border)] bg-background p-6">
        <Skeleton className="h-6 w-72" />
        <div className="mt-4 grid gap-3 md:grid-cols-4">
          {Array.from({ length: 4 }).map((_, index) => (
            <Skeleton key={index} className="h-24 rounded-2xl" />
          ))}
        </div>
      </section>
    );
  }

  if (error || !packet) {
    return (
      <section className="rounded-3xl border border-[var(--color-border)] bg-background p-6">
        <h2 className="text-xl font-semibold text-[var(--color-foreground)]">Release-candidate packet</h2>
        <p className="mt-2 text-sm text-[var(--color-muted-foreground)]">
          Release-candidate evidence is unavailable. Keep this course in internal review until the read packet responds.
        </p>
      </section>
    );
  }

  const ready = packet.decision === "READY_FOR_RELEASE_CANDIDATE_REVIEW";
  const lessonTypes = Object.entries(packet.evidence.structure.counts.lessonTypes)
    .map(([type, count]) => `${type}: ${count}`)
    .join(", ");

  return (
    <section className="rounded-3xl border border-[var(--color-border)] bg-background p-6">
      <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[var(--color-muted-foreground)]">Release-candidate packet</p>
          <h2 className="mt-2 text-2xl font-semibold text-[var(--color-foreground)]">
            {ready ? "Ready for release-candidate review" : "Release-candidate evidence incomplete"}
          </h2>
          <p className="mt-2 max-w-2xl text-sm leading-6 text-[var(--color-muted-foreground)]">
            Staff-only aggregate read packet for Stero course review. It combines structure, review, video, readiness, progress, and certificate evidence without approving learner serving or target writes.
          </p>
        </div>
        <span className={`rounded-full border px-3 py-1 text-xs font-semibold ${
          ready
            ? "border-[var(--color-success)]/30 text-[var(--color-success)]"
            : "border-[var(--color-warning)]/30 text-[var(--color-warning)]"
        }`}
        >
          {packet.recommendedMode}
        </span>
      </div>

      <dl className="mt-6 grid gap-3 text-sm md:grid-cols-4">
        <div className="rounded-2xl bg-[var(--color-surface)] p-4">
          <dt className="text-[11px] uppercase tracking-[0.14em] text-[var(--color-muted-foreground)]">Serving allowed</dt>
          <dd className="mt-1 text-lg font-semibold text-[var(--color-foreground)]">{packet.learnerServingAllowed ? "Yes" : "No"}</dd>
        </div>
        <div className="rounded-2xl bg-[var(--color-surface)] p-4">
          <dt className="text-[11px] uppercase tracking-[0.14em] text-[var(--color-muted-foreground)]">Target writes</dt>
          <dd className="mt-1 text-lg font-semibold text-[var(--color-foreground)]">{packet.targetWriteBehavior}</dd>
        </div>
        <div className="rounded-2xl bg-[var(--color-surface)] p-4">
          <dt className="text-[11px] uppercase tracking-[0.14em] text-[var(--color-muted-foreground)]">Lessons linked</dt>
          <dd className="mt-1 text-lg font-semibold text-[var(--color-foreground)]">
            {packet.evidence.structure.counts.linkedPages}/{packet.evidence.structure.counts.lessons}
          </dd>
        </div>
        <div className="rounded-2xl bg-[var(--color-surface)] p-4">
          <dt className="text-[11px] uppercase tracking-[0.14em] text-[var(--color-muted-foreground)]">Trusted video embeds</dt>
          <dd className="mt-1 text-lg font-semibold text-[var(--color-foreground)]">
            {packet.evidence.videoDelivery.summary.trustedEmbeds}/{packet.evidence.videoDelivery.summary.videoLessons}
          </dd>
        </div>
      </dl>

      <div className="mt-5 grid gap-4 lg:grid-cols-[1.1fr_0.9fr]">
        <div className={`rounded-2xl border p-4 ${
          packet.coreShadowReadiness.readPathReady
            ? "border-[var(--color-success)]/30 bg-[var(--color-success)]/5"
            : "border-[var(--color-warning)]/30 bg-[var(--color-warning)]/5"
        }`}
        >
          <h3 className="text-sm font-semibold text-[var(--color-foreground)]">
            {packet.coreShadowReadiness.readPathReady ? "Core LMS shadow path ready" : "Core LMS shadow path blocked"}
          </h3>
          <p className="mt-2 text-sm text-[var(--color-muted-foreground)]">{packet.coreShadowReadiness.summary}</p>
          {packet.coreShadowReadiness.advisoryBlockers.length > 0 ? (
            <p className="mt-3 text-xs text-[var(--color-muted-foreground)]">
              Media advisory only: {packet.coreShadowReadiness.advisoryBlockers.join(", ")}
            </p>
          ) : null}
        </div>

        <div className={`rounded-2xl border p-4 ${
          ready
            ? "border-[var(--color-success)]/30 bg-[var(--color-success)]/5"
            : "border-[var(--color-warning)]/30 bg-[var(--color-warning)]/5"
        }`}
        >
          <h3 className="text-sm font-semibold text-[var(--color-foreground)]">
            {ready ? "Evidence packet assembled" : "Release blockers"}
          </h3>
          {packet.blockers.length === 0 ? (
            <p className="mt-2 text-sm text-[var(--color-foreground)]">
              Structure, page review, video delivery, certificate, and internal readiness evidence are complete for staff release-candidate review.
            </p>
          ) : (
            <ul className="mt-3 space-y-2 text-sm text-[var(--color-muted-foreground)]">
              {packet.blockers.map((blocker) => (
                <li key={blocker}>{blocker}</li>
              ))}
            </ul>
          )}
        </div>

        <div className="rounded-2xl border border-[var(--color-border)] bg-[var(--color-surface)] p-4">
          <h3 className="text-sm font-semibold text-[var(--color-foreground)]">Still blocked after this packet</h3>
          <ul className="mt-3 space-y-2 text-sm text-[var(--color-muted-foreground)]">
            <li>Separate serving approval required: {packet.requiresSeparateServingApproval ? "Yes" : "No"}</li>
            <li>Learner serving remains off.</li>
            <li>Target writes remain none.</li>
          </ul>
        </div>
      </div>

      <div className="mt-5 grid gap-3 text-sm md:grid-cols-2 xl:grid-cols-4">
        <div className="rounded-2xl border border-[var(--color-border)] bg-[var(--color-surface)] p-4">
          <p className="text-xs font-semibold uppercase tracking-[0.14em] text-[var(--color-muted-foreground)]">Structure</p>
          <p className="mt-2 font-semibold text-[var(--color-foreground)]">
            {packet.evidence.structure.counts.modules} modules · {packet.evidence.structure.counts.lessons} lessons
          </p>
          <p className="mt-1 text-xs text-[var(--color-muted-foreground)]">{lessonTypes || "No lesson types mounted"}</p>
        </div>
        <div className="rounded-2xl border border-[var(--color-border)] bg-[var(--color-surface)] p-4">
          <p className="text-xs font-semibold uppercase tracking-[0.14em] text-[var(--color-muted-foreground)]">Page review</p>
          <p className="mt-2 font-semibold text-[var(--color-foreground)]">{packet.evidence.review.decision}</p>
          <p className="mt-1 text-xs text-[var(--color-muted-foreground)]">
            {packet.evidence.review.counts.approvedCurrentReviews} approved · {packet.evidence.review.blockers.length} blockers
          </p>
        </div>
        <div className="rounded-2xl border border-[var(--color-border)] bg-[var(--color-surface)] p-4">
          <p className="text-xs font-semibold uppercase tracking-[0.14em] text-[var(--color-muted-foreground)]">Video delivery</p>
          <p className="mt-2 font-semibold text-[var(--color-foreground)]">{packet.evidence.videoDelivery.decision}</p>
          <p className="mt-1 text-xs text-[var(--color-muted-foreground)]">
            {packet.evidence.videoDelivery.summary.sandboxedEmbeds} sandboxed · {packet.evidence.videoDelivery.blockers.length} blockers
          </p>
        </div>
        <div className="rounded-2xl border border-[var(--color-border)] bg-[var(--color-surface)] p-4">
          <p className="text-xs font-semibold uppercase tracking-[0.14em] text-[var(--color-muted-foreground)]">Progress</p>
          <p className="mt-2 font-semibold text-[var(--color-foreground)]">{packet.evidence.progress.counts.enrollments} enrollments</p>
          <p className="mt-1 text-xs text-[var(--color-muted-foreground)]">
            {packet.evidence.progress.counts.completed} completed · {packet.evidence.progress.counts.active} active
          </p>
          <p className="mt-1 text-xs text-[var(--color-muted-foreground)]">
            {packet.evidence.progress.activity.startedLearnerLessons} learner-lessons started · {packet.evidence.progress.activity.startToCompletionRatePercent}% start-to-completion
          </p>
          <p className="mt-1 text-xs text-[var(--color-muted-foreground)]">
            {packet.evidence.progress.activity.exerciseSubmissions} exercise submissions observed
          </p>
          <p className="mt-1 text-xs text-[var(--color-muted-foreground)]">
            {packet.evidence.progress.activity.courseCompletionEvents} course completion events
          </p>
        </div>
        <div className="rounded-2xl border border-[var(--color-border)] bg-[var(--color-surface)] p-4">
          <p className="text-xs font-semibold uppercase tracking-[0.14em] text-[var(--color-muted-foreground)]">Learner operations</p>
          <p className="mt-2 font-semibold text-[var(--color-foreground)]">{packet.evidence.learnerOperations.decision}</p>
          <p className="mt-1 text-xs text-[var(--color-muted-foreground)]">
            {packet.evidence.learnerOperations.riskSegments.needsNudge} need nudge · {packet.evidence.learnerOperations.riskSegments.readyForCompletionNudge} near completion
          </p>
          <p className="mt-1 text-xs text-[var(--color-muted-foreground)]">
            identity hidden · assignment writes blocked
          </p>
        </div>
        <div className="rounded-2xl border border-[var(--color-border)] bg-[var(--color-surface)] p-4">
          <p className="text-xs font-semibold uppercase tracking-[0.14em] text-[var(--color-muted-foreground)]">Quiz runtime</p>
          <p className="mt-2 font-semibold text-[var(--color-foreground)]">{packet.evidence.quizRuntime.configuredQuizLessons} configured</p>
          <p className="mt-1 text-xs text-[var(--color-muted-foreground)]">
            Preview {packet.evidence.quizRuntime.learnerPreviewMode.replaceAll("_", " ")} · attempts {packet.evidence.quizRuntime.learnerAttemptSummaryEnabled ? "visible" : "blocked"} · submit action {packet.evidence.learnerPortalCapabilities.quizSubmitActionContractMounted ? "contracted" : "missing"}
          </p>
          <p className="mt-1 text-xs text-[var(--color-muted-foreground)]">
            {packet.evidence.progress.activity.quizAttempts} quiz attempts observed · submit flag blocked
          </p>
        </div>
        <div className="rounded-2xl border border-[var(--color-border)] bg-[var(--color-surface)] p-4">
          <p className="text-xs font-semibold uppercase tracking-[0.14em] text-[var(--color-muted-foreground)]">Certificate readiness</p>
          <p className="mt-2 font-semibold text-[var(--color-foreground)]">{packet.evidence.certificateReadiness.decision}</p>
          <p className="mt-1 text-xs text-[var(--color-muted-foreground)]">
            {packet.evidence.certificateReadiness.counts.certificateTokensPresent} tokens present · {packet.evidence.certificateReadiness.counts.certificateGeneratedEvents} generated events
          </p>
          <p className="mt-1 text-xs text-[var(--color-muted-foreground)]">
            token values hidden · public verify blocked
          </p>
        </div>
        <div className="rounded-2xl border border-[var(--color-border)] bg-[var(--color-surface)] p-4">
          <p className="text-xs font-semibold uppercase tracking-[0.14em] text-[var(--color-muted-foreground)]">Exercise runtime</p>
          <p className="mt-2 font-semibold text-[var(--color-foreground)]">
            {packet.evidence.exerciseRuntime.publishedExerciseLessons}/{packet.evidence.exerciseRuntime.exerciseLessons} published
          </p>
          <p className="mt-1 text-xs text-[var(--color-muted-foreground)]">
            Read {packet.evidence.exerciseRuntime.learnerReadMode.replaceAll("_", " ")} · submit blocked · progress writes blocked
          </p>
        </div>
        <div className="rounded-2xl border border-[var(--color-border)] bg-[var(--color-surface)] p-4">
          <p className="text-xs font-semibold uppercase tracking-[0.14em] text-[var(--color-muted-foreground)]">Learner portal spine</p>
          <p className="mt-2 font-semibold text-[var(--color-foreground)]">
            {packet.evidence.learnerPortalCapabilities.steroShadowMode ? "Stero shadow" : "Not mounted"}
          </p>
          <p className="mt-1 text-xs text-[var(--color-muted-foreground)]">
            Timeline {packet.evidence.learnerPortalCapabilities.learnerActivityTimelineReadEnabled ? "mounted" : "blocked"} · exercise action {packet.evidence.learnerPortalCapabilities.exerciseSubmitActionContractMounted ? "contracted" : "missing"} · English Ease primary
          </p>
        </div>
      </div>
    </section>
  );
}

function LearnerServingPilotGatePanel({
  gate,
  isLoading,
  error,
  enabled,
}: {
  gate: CourseLearnerServingPilotGateResponse | null | undefined;
  isLoading: boolean;
  error: unknown;
  enabled: boolean;
}) {
  if (!enabled) return null;

  if (isLoading) {
    return (
      <section className="rounded-3xl border border-[var(--color-border)] bg-background p-6">
        <Skeleton className="h-6 w-80" />
        <div className="mt-4 grid gap-3 md:grid-cols-3">
          {Array.from({ length: 3 }).map((_, index) => (
            <Skeleton key={index} className="h-24 rounded-2xl" />
          ))}
        </div>
      </section>
    );
  }

  if (error || !gate) {
    return (
      <section className="rounded-3xl border border-[var(--color-border)] bg-background p-6">
        <h2 className="text-xl font-semibold text-[var(--color-foreground)]">Learner-serving pilot gate</h2>
        <p className="mt-2 text-sm text-[var(--color-muted-foreground)]">
          Pilot gate evidence is unavailable. Keep learner-facing routes off until this staff-only read gate responds.
        </p>
      </section>
    );
  }

  const ready = gate.decision === "READY_FOR_SESSION_1_LEARNER_SERVING_PILOT_REVIEW";

  return (
    <section className="rounded-3xl border border-[var(--color-border)] bg-background p-6">
      <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[var(--color-muted-foreground)]">Learner-serving pilot gate</p>
          <h2 className="mt-2 text-2xl font-semibold text-[var(--color-foreground)]">
            {ready ? "Ready for Session 1 pilot review" : "Pilot gate blocked"}
          </h2>
          <p className="mt-2 max-w-2xl text-sm leading-6 text-[var(--color-muted-foreground)]">
            Staff-only gate for the next safe course cutover step. It validates release-candidate evidence for review while keeping public learner serving, course publishing, learner writes, and AI runtime serving off.
          </p>
        </div>
        <span className={`rounded-full border px-3 py-1 text-xs font-semibold ${
          ready
            ? "border-[var(--color-success)]/30 text-[var(--color-success)]"
            : "border-[var(--color-warning)]/30 text-[var(--color-warning)]"
        }`}
        >
          {gate.recommendedMode}
        </span>
      </div>

      <dl className="mt-6 grid gap-3 text-sm md:grid-cols-4">
        <div className="rounded-2xl bg-[var(--color-surface)] p-4">
          <dt className="text-[11px] uppercase tracking-[0.14em] text-[var(--color-muted-foreground)]">Pilot review</dt>
          <dd className="mt-1 text-lg font-semibold text-[var(--color-foreground)]">{ready ? "Ready" : "Blocked"}</dd>
        </div>
        <div className="rounded-2xl bg-[var(--color-surface)] p-4">
          <dt className="text-[11px] uppercase tracking-[0.14em] text-[var(--color-muted-foreground)]">Serving still off</dt>
          <dd className="mt-1 text-lg font-semibold text-[var(--color-foreground)]">{gate.learnerServingAllowed ? "No" : "Yes"}</dd>
        </div>
        <div className="rounded-2xl bg-[var(--color-surface)] p-4">
          <dt className="text-[11px] uppercase tracking-[0.14em] text-[var(--color-muted-foreground)]">Shadow starts</dt>
          <dd className="mt-1 text-lg font-semibold text-[var(--color-foreground)]">
            {gate.evidence.releaseCandidate.counts.startedLearnerLessons}
          </dd>
          <dd className="mt-1 text-xs text-[var(--color-muted-foreground)]">
            {gate.evidence.releaseCandidate.counts.startToCompletionRatePercent}% start-to-completion
          </dd>
          <dd className="mt-1 text-xs text-[var(--color-muted-foreground)]">
            {gate.evidence.releaseCandidate.counts.courseCompletionEvents} course completion events
          </dd>
        </div>
        <div className="rounded-2xl bg-[var(--color-surface)] p-4">
          <dt className="text-[11px] uppercase tracking-[0.14em] text-[var(--color-muted-foreground)]">Page reviews</dt>
          <dd className="mt-1 text-lg font-semibold text-[var(--color-foreground)]">
            {gate.evidence.releaseCandidate.counts.approvedCurrentReviews} approved
          </dd>
        </div>
        <div className="rounded-2xl bg-[var(--color-surface)] p-4">
          <dt className="text-[11px] uppercase tracking-[0.14em] text-[var(--color-muted-foreground)]">Quiz action</dt>
          <dd className="mt-1 text-lg font-semibold text-[var(--color-foreground)]">
            {gate.evidence.releaseCandidate.counts.quizSubmitActionContractMounted ? "Contracted" : "Missing"}
          </dd>
          <dd className="mt-1 text-xs text-[var(--color-muted-foreground)]">
            {gate.evidence.releaseCandidate.counts.quizAttempts} attempts observed · flag {gate.evidence.releaseCandidate.counts.quizSubmitEnabled ? "enabled" : "blocked"}
          </dd>
        </div>
        <div className="rounded-2xl bg-[var(--color-surface)] p-4">
          <dt className="text-[11px] uppercase tracking-[0.14em] text-[var(--color-muted-foreground)]">Portal reads</dt>
          <dd className="mt-1 text-lg font-semibold text-[var(--color-foreground)]">
            {gate.evidence.releaseCandidate.counts.learnerActivityTimelineReadEnabled ? "Mounted" : "Blocked"}
          </dd>
          <dd className="mt-1 text-xs text-[var(--color-muted-foreground)]">
            dashboard · lessons · timeline · {gate.evidence.releaseCandidate.counts.exerciseSubmissions} exercise submissions
          </dd>
        </div>
        <div className="rounded-2xl bg-[var(--color-surface)] p-4">
          <dt className="text-[11px] uppercase tracking-[0.14em] text-[var(--color-muted-foreground)]">Learner ops</dt>
          <dd className="mt-1 text-lg font-semibold text-[var(--color-foreground)]">
            {gate.evidence.releaseCandidate.counts.learnerOperationsReadEnabled ? "Mounted" : "Blocked"}
          </dd>
          <dd className="mt-1 text-xs text-[var(--color-muted-foreground)]">
            {gate.evidence.releaseCandidate.counts.needsNudge} need nudge · assignment writes blocked
          </dd>
        </div>
        <div className="rounded-2xl bg-[var(--color-surface)] p-4">
          <dt className="text-[11px] uppercase tracking-[0.14em] text-[var(--color-muted-foreground)]">Certificates</dt>
          <dd className="mt-1 text-lg font-semibold text-[var(--color-foreground)]">
            {gate.evidence.releaseCandidate.counts.certificateReadinessConsistent ? "Consistent" : "Blocked"}
          </dd>
          <dd className="mt-1 text-xs text-[var(--color-muted-foreground)]">
            {gate.evidence.releaseCandidate.counts.certificateTokensPresent} tokens · values hidden · public verify off
          </dd>
        </div>
      </dl>

      <div className="mt-5 grid gap-4 lg:grid-cols-[1fr_1fr]">
        <div className={`rounded-2xl border p-4 ${
          ready
            ? "border-[var(--color-success)]/30 bg-[var(--color-success)]/5"
            : "border-[var(--color-warning)]/30 bg-[var(--color-warning)]/5"
        }`}
        >
          <h3 className="text-sm font-semibold text-[var(--color-foreground)]">
            {ready ? "Next safe action" : "Pilot blockers"}
          </h3>
          {gate.blockers.length === 0 ? (
            <ul className="mt-3 space-y-2 text-sm text-[var(--color-muted-foreground)]">
              {gate.allowedNextActions.map((action) => (
                <li key={action}>{action}</li>
              ))}
            </ul>
          ) : (
            <ul className="mt-3 space-y-2 text-sm text-[var(--color-muted-foreground)]">
              {gate.blockers.map((blocker) => (
                <li key={blocker}>{blocker}</li>
              ))}
            </ul>
          )}
        </div>

        <div className="rounded-2xl border border-[var(--color-border)] bg-[var(--color-surface)] p-4">
          <h3 className="text-sm font-semibold text-[var(--color-foreground)]">Hard stops still enforced</h3>
          <ul className="mt-3 space-y-2 text-sm text-[var(--color-muted-foreground)]">
            <li>Explicit Session 1 pilot approval required: {gate.requiresExplicitPilotApproval ? "Yes" : "No"}</li>
            <li>Separate route enablement change required: {gate.requiresSeparateRouteEnablementChange ? "Yes" : "No"}</li>
            <li>Post-enable smoke required: {gate.requiresPostEnablementSmoke ? "Yes" : "No"}</li>
            <li>Target writes: {gate.targetWriteBehavior}</li>
          </ul>
        </div>
      </div>
    </section>
  );
}

function DualRunSmokePacketPanel({
  packet,
  isLoading,
  error,
  enabled,
}: {
  packet: CourseDualRunSmokePacketResponse | null | undefined;
  isLoading: boolean;
  error: unknown;
  enabled: boolean;
}) {
  if (!enabled) return null;

  if (isLoading) {
    return (
      <section className="rounded-3xl border border-[var(--color-border)] bg-background p-6">
        <Skeleton className="h-6 w-80" />
        <div className="mt-4 grid gap-3 md:grid-cols-4">
          {Array.from({ length: 4 }).map((_, index) => (
            <Skeleton key={index} className="h-24 rounded-2xl" />
          ))}
        </div>
      </section>
    );
  }

  if (error || !packet) {
    return (
      <section className="rounded-3xl border border-[var(--color-border)] bg-background p-6">
        <h2 className="text-xl font-semibold text-[var(--color-foreground)]">Dual-run smoke packet</h2>
        <p className="mt-2 text-sm text-[var(--color-muted-foreground)]">
          Dual-run smoke evidence is unavailable. Keep Stero in shadow mode and keep English Ease primary.
        </p>
      </section>
    );
  }

  const ready = packet.decision === "READY_FOR_DUAL_RUN_SMOKE_OBSERVATION";
  const passedChecks = packet.checks.filter((check) => check.ok).length;
  const writeLocks = packet.surfaces.lockedWrites;
  const allWriteLocksVisible = writeLocks.assignment
    && writeLocks.quizSubmit
    && writeLocks.exerciseSubmit
    && writeLocks.exerciseProgress
    && writeLocks.certificateIssue
    && writeLocks.targetWrites;

  return (
    <section className="rounded-3xl border border-[var(--color-border)] bg-background p-6">
      <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[var(--color-muted-foreground)]">Dual-run smoke packet</p>
          <h2 className="mt-2 text-2xl font-semibold text-[var(--color-foreground)]">
            {ready ? "Ready for staff dual-run smoke" : "Dual-run smoke blocked"}
          </h2>
          <p className="mt-2 max-w-2xl text-sm leading-6 text-[var(--color-muted-foreground)]">
            Staff-only smoke packet for observing Stero processing while the existing English Ease runtime remains primary. It does not approve public serving, target writes, parity, or cutover.
          </p>
        </div>
        <span className={`rounded-full border px-3 py-1 text-xs font-semibold ${
          ready
            ? "border-[var(--color-success)]/30 text-[var(--color-success)]"
            : "border-[var(--color-warning)]/30 text-[var(--color-warning)]"
        }`}
        >
          {packet.decision}
        </span>
      </div>

      <dl className="mt-6 grid gap-3 text-sm md:grid-cols-4">
        <div className="rounded-2xl bg-[var(--color-surface)] p-4">
          <dt className="text-[11px] uppercase tracking-[0.14em] text-[var(--color-muted-foreground)]">Stero runtime</dt>
          <dd className="mt-1 text-lg font-semibold text-[var(--color-foreground)]">{packet.mode.steroShadowMode ? "Shadow" : "Not shadow"}</dd>
          <dd className="mt-1 text-xs text-[var(--color-muted-foreground)]">English Ease primary: {packet.mode.existingRuntimePrimaryMode ? "yes" : "no"}</dd>
        </div>
        <div className="rounded-2xl bg-[var(--color-surface)] p-4">
          <dt className="text-[11px] uppercase tracking-[0.14em] text-[var(--color-muted-foreground)]">Required reads</dt>
          <dd className="mt-1 text-lg font-semibold text-[var(--color-foreground)]">{packet.smokeSummary.requiredReadsReady ? "Ready" : "Blocked"}</dd>
          <dd className="mt-1 text-xs text-[var(--color-muted-foreground)]">Required reads ready: {packet.smokeSummary.requiredReadsReady ? "yes" : "no"}</dd>
        </div>
        <div className="rounded-2xl bg-[var(--color-surface)] p-4">
          <dt className="text-[11px] uppercase tracking-[0.14em] text-[var(--color-muted-foreground)]">Write locks</dt>
          <dd className="mt-1 text-lg font-semibold text-[var(--color-foreground)]">{packet.smokeSummary.writeLocksHeld ? "Held" : "Open"}</dd>
          <dd className="mt-1 text-xs text-[var(--color-muted-foreground)]">Target writes locked: {writeLocks.targetWrites ? "yes" : "no"}</dd>
        </div>
        <div className="rounded-2xl bg-[var(--color-surface)] p-4">
          <dt className="text-[11px] uppercase tracking-[0.14em] text-[var(--color-muted-foreground)]">Public cutover</dt>
          <dd className="mt-1 text-lg font-semibold text-[var(--color-foreground)]">{packet.mode.publicCutoverAllowed ? "Allowed" : "Blocked"}</dd>
          <dd className="mt-1 text-xs text-[var(--color-muted-foreground)]">public cutover claim {packet.mode.cutoverClaim}</dd>
        </div>
        <div className="rounded-2xl bg-[var(--color-surface)] p-4">
          <dt className="text-[11px] uppercase tracking-[0.14em] text-[var(--color-muted-foreground)]">Learner activity</dt>
          <dd className="mt-1 text-lg font-semibold text-[var(--color-foreground)]">{packet.smokeSummary.observedLearnerLessons}</dd>
          <dd className="mt-1 text-xs text-[var(--color-muted-foreground)]">
            {packet.smokeSummary.observedLessonCompletions} lesson completions · {packet.smokeSummary.observedCourseCompletions} course completions
          </dd>
        </div>
        <div className="rounded-2xl bg-[var(--color-surface)] p-4">
          <dt className="text-[11px] uppercase tracking-[0.14em] text-[var(--color-muted-foreground)]">Quiz and exercise</dt>
          <dd className="mt-1 text-lg font-semibold text-[var(--color-foreground)]">
            {packet.smokeSummary.quizPreviewReady && packet.smokeSummary.exerciseSubmissionReady ? "Contracted" : "Incomplete"}
          </dd>
          <dd className="mt-1 text-xs text-[var(--color-muted-foreground)]">
            {packet.smokeSummary.observedQuizAttempts} quiz attempts · {packet.smokeSummary.observedExerciseSubmissions} exercise submissions
          </dd>
        </div>
        <div className="rounded-2xl bg-[var(--color-surface)] p-4">
          <dt className="text-[11px] uppercase tracking-[0.14em] text-[var(--color-muted-foreground)]">Safety checks</dt>
          <dd className="mt-1 text-lg font-semibold text-[var(--color-foreground)]">{passedChecks}/{packet.checks.length}</dd>
          <dd className="mt-1 text-xs text-[var(--color-muted-foreground)]">learner identity hidden: {packet.smokeSummary.learnerIdentityHidden ? "yes" : "no"}</dd>
        </div>
        <div className="rounded-2xl bg-[var(--color-surface)] p-4">
          <dt className="text-[11px] uppercase tracking-[0.14em] text-[var(--color-muted-foreground)]">Source snapshot</dt>
          <dd className="mt-1 text-lg font-semibold text-[var(--color-foreground)]">{packet.sourceSnapshot.decision.replaceAll("_", " ")}</dd>
          <dd className="mt-1 text-xs text-[var(--color-muted-foreground)]">pilot: {packet.sourceSnapshot.pilotGateDecision.replaceAll("_", " ")}</dd>
        </div>
      </dl>

      <div className="mt-5 grid gap-4 lg:grid-cols-[1fr_1fr]">
        <div className={`rounded-2xl border p-4 ${
          ready
            ? "border-[var(--color-success)]/30 bg-[var(--color-success)]/5"
            : "border-[var(--color-warning)]/30 bg-[var(--color-warning)]/5"
        }`}
        >
          <h3 className="text-sm font-semibold text-[var(--color-foreground)]">
            {ready ? "Staff smoke actions" : "Smoke blockers"}
          </h3>
          {packet.blockers.length === 0 ? (
            <ul className="mt-3 space-y-2 text-sm text-[var(--color-muted-foreground)]">
              {packet.allowedNextActions.map((action) => (
                <li key={action}>{action}</li>
              ))}
            </ul>
          ) : (
            <ul className="mt-3 space-y-2 text-sm text-[var(--color-muted-foreground)]">
              {packet.blockers.map((blocker) => (
                <li key={blocker}>{blocker}</li>
              ))}
            </ul>
          )}
        </div>

        <div className="rounded-2xl border border-[var(--color-border)] bg-[var(--color-surface)] p-4">
          <h3 className="text-sm font-semibold text-[var(--color-foreground)]">Mutation boundary</h3>
          <ul className="mt-3 space-y-2 text-sm text-[var(--color-muted-foreground)]">
            <li>All write locks visible: {allWriteLocksVisible ? "Yes" : "No"}</li>
            <li>Assignment writes: {writeLocks.assignment ? "locked" : "open"}</li>
            <li>Quiz submit writes: {writeLocks.quizSubmit ? "locked" : "open"}</li>
            <li>Exercise submit writes: {writeLocks.exerciseSubmit ? "locked" : "open"}</li>
            <li>Exercise progress writes: {writeLocks.exerciseProgress ? "locked" : "open"}</li>
            <li>Certificate issue: {writeLocks.certificateIssue ? "locked" : "open"}</li>
            <li>Target writes: {packet.mode.targetWriteBehavior}</li>
          </ul>
        </div>
      </div>

      {packet.requiredCutoverApprovals.length > 0 ? (
        <div className="mt-5 rounded-2xl border border-[var(--color-border)] bg-[var(--color-surface)] p-4">
          <h3 className="text-sm font-semibold text-[var(--color-foreground)]">Approvals still required</h3>
          <ul className="mt-3 space-y-2 text-sm text-[var(--color-muted-foreground)]">
            {packet.requiredCutoverApprovals.map((approval) => (
              <li key={approval}>{approval}</li>
            ))}
          </ul>
        </div>
      ) : null}
    </section>
  );
}

function CutoverReadinessSnapshotPanel({
  snapshot,
  isLoading,
  error,
  enabled,
}: {
  snapshot: CourseCutoverReadinessSnapshotResponse | null | undefined;
  isLoading: boolean;
  error: unknown;
  enabled: boolean;
}) {
  if (!enabled) return null;

  if (isLoading) {
    return (
      <section className="rounded-3xl border border-[var(--color-border)] bg-background p-6">
        <Skeleton className="h-6 w-80" />
        <div className="mt-4 grid gap-3 md:grid-cols-4">
          {Array.from({ length: 4 }).map((_, index) => (
            <Skeleton key={index} className="h-24 rounded-2xl" />
          ))}
        </div>
      </section>
    );
  }

  if (error || !snapshot) {
    return (
      <section className="rounded-3xl border border-[var(--color-border)] bg-background p-6">
        <h2 className="text-xl font-semibold text-[var(--color-foreground)]">Cutover readiness snapshot</h2>
        <p className="mt-2 text-sm text-[var(--color-muted-foreground)]">
          Cutover evidence is unavailable. Keep Stero in shadow mode.
        </p>
      </section>
    );
  }

  const ready = snapshot.decision === "READY_FOR_DUAL_RUN_OBSERVATION";

  return (
    <section className="rounded-3xl border border-[var(--color-border)] bg-background p-6">
      <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[var(--color-muted-foreground)]">Cutover readiness</p>
          <h2 className="mt-2 text-2xl font-semibold text-[var(--color-foreground)]">
            {ready ? "Ready for dual-run observation" : "Not ready to cut over"}
          </h2>
          <p className="mt-2 max-w-2xl text-sm leading-6 text-[var(--color-muted-foreground)]">
            One staff-only snapshot for the English Ease to Stero LMS migration. Stero remains shadow; English Ease remains primary.
          </p>
        </div>
        <span className={`rounded-full border px-3 py-1 text-xs font-semibold ${
          ready
            ? "border-[var(--color-success)]/30 text-[var(--color-success)]"
            : "border-[var(--color-warning)]/30 text-[var(--color-warning)]"
        }`}
        >
          {snapshot.decision}
        </span>
      </div>
      <dl className="mt-6 grid gap-3 text-sm md:grid-cols-4">
        <div className="rounded-2xl bg-[var(--color-surface)] p-4">
          <dt className="text-[11px] uppercase tracking-[0.14em] text-[var(--color-muted-foreground)]">Stero mode</dt>
          <dd className="mt-1 text-lg font-semibold text-[var(--color-foreground)]">{snapshot.dualRunStatus.steroShadowMode ? "Shadow" : "Primary"}</dd>
        </div>
        <div className="rounded-2xl bg-[var(--color-surface)] p-4">
          <dt className="text-[11px] uppercase tracking-[0.14em] text-[var(--color-muted-foreground)]">Lesson writes</dt>
          <dd className="mt-1 text-lg font-semibold text-[var(--color-foreground)]">
            {snapshot.dualRunStatus.learnerOwnedWritesMounted.lessonCompletion ? "Mounted" : "Blocked"}
          </dd>
          <dd className="mt-1 text-xs text-[var(--color-muted-foreground)]">
            course completion {snapshot.dualRunStatus.learnerOwnedWritesMounted.courseCompletion ? "observed" : "not observed"}
          </dd>
        </div>
        <div className="rounded-2xl bg-[var(--color-surface)] p-4">
          <dt className="text-[11px] uppercase tracking-[0.14em] text-[var(--color-muted-foreground)]">Quiz reads</dt>
          <dd className="mt-1 text-lg font-semibold text-[var(--color-foreground)]">
            {snapshot.dualRunStatus.learnerOwnedReadsMounted.quizAttemptSummary ? "Mounted" : "Blocked"}
          </dd>
          <dd className="mt-1 text-xs text-[var(--color-muted-foreground)]">
            submit action {snapshot.dualRunStatus.learnerOwnedReadsMounted.quizSubmitActionContract ? "contracted" : "missing"}
          </dd>
        </div>
        <div className="rounded-2xl bg-[var(--color-surface)] p-4">
          <dt className="text-[11px] uppercase tracking-[0.14em] text-[var(--color-muted-foreground)]">Continuation reads</dt>
          <dd className="mt-1 text-lg font-semibold text-[var(--color-foreground)]">
            {snapshot.dualRunStatus.learnerOwnedReadsMounted.nextLesson ? "Mounted" : "Blocked"}
          </dd>
          <dd className="mt-1 text-xs text-[var(--color-muted-foreground)]">
            action {snapshot.dualRunStatus.learnerOwnedReadsMounted.learnerContinuationActionContract ? "contracted" : "missing"}
          </dd>
        </div>
        <div className="rounded-2xl bg-[var(--color-surface)] p-4">
          <dt className="text-[11px] uppercase tracking-[0.14em] text-[var(--color-muted-foreground)]">Timeline reads</dt>
          <dd className="mt-1 text-lg font-semibold text-[var(--color-foreground)]">
            {snapshot.dualRunStatus.learnerOwnedReadsMounted.learnerActivityTimeline ? "Mounted" : "Blocked"}
          </dd>
        </div>
        <div className="rounded-2xl bg-[var(--color-surface)] p-4">
          <dt className="text-[11px] uppercase tracking-[0.14em] text-[var(--color-muted-foreground)]">Learner ops</dt>
          <dd className="mt-1 text-lg font-semibold text-[var(--color-foreground)]">
            {snapshot.dualRunStatus.learnerOwnedReadsMounted.learnerOperations ? "Mounted" : "Blocked"}
          </dd>
          <dd className="mt-1 text-xs text-[var(--color-muted-foreground)]">
            completion action {snapshot.dualRunStatus.learnerOwnedReadsMounted.courseCompletionActionContract ? "contracted" : "missing"} · assignment write {snapshot.dualRunStatus.learnerOwnedWritesMounted.assignment ? "mounted" : "blocked"}
          </dd>
        </div>
        <div className="rounded-2xl bg-[var(--color-surface)] p-4">
          <dt className="text-[11px] uppercase tracking-[0.14em] text-[var(--color-muted-foreground)]">Exercise reads</dt>
          <dd className="mt-1 text-lg font-semibold text-[var(--color-foreground)]">
            {snapshot.dualRunStatus.learnerOwnedReadsMounted.exerciseContent ? "Mounted" : "Blocked"}
          </dd>
          <dd className="mt-1 text-xs text-[var(--color-muted-foreground)]">
            action {snapshot.dualRunStatus.learnerOwnedReadsMounted.exerciseSubmitActionContract ? "contracted" : "missing"} · submit write {snapshot.dualRunStatus.learnerOwnedWritesMounted.exerciseSubmit ? "mounted" : "blocked"} · observed {snapshot.evidence.counts.exerciseSubmitWriteEvidenceObserved ? "yes" : "no"}
          </dd>
        </div>
        <div className="rounded-2xl bg-[var(--color-surface)] p-4">
          <dt className="text-[11px] uppercase tracking-[0.14em] text-[var(--color-muted-foreground)]">Certificate reads</dt>
          <dd className="mt-1 text-lg font-semibold text-[var(--color-foreground)]">
            {snapshot.dualRunStatus.learnerOwnedReadsMounted.certificateReadiness ? "Consistent" : "Blocked"}
          </dd>
          <dd className="mt-1 text-xs text-[var(--color-muted-foreground)]">
            eligibility {snapshot.dualRunStatus.learnerOwnedReadsMounted.certificateEligibility ? "mounted" : "blocked"} · token presence {snapshot.dualRunStatus.learnerOwnedReadsMounted.certificateTokenPresence ? "mounted" : "blocked"}
          </dd>
        </div>
        <div className="rounded-2xl bg-[var(--color-surface)] p-4">
          <dt className="text-[11px] uppercase tracking-[0.14em] text-[var(--color-muted-foreground)]">Public cutover</dt>
          <dd className="mt-1 text-lg font-semibold text-[var(--color-foreground)]">{snapshot.publicCutoverAllowed ? "Allowed" : "Blocked"}</dd>
        </div>
      </dl>
      <div className="mt-5 rounded-2xl border border-[var(--color-border)] bg-[var(--color-surface)] p-4">
        <h3 className="text-sm font-semibold text-[var(--color-foreground)]">Current blockers</h3>
        <ul className="mt-3 space-y-2 text-sm text-[var(--color-muted-foreground)]">
          {snapshot.blockers.map((blocker) => (
            <li key={blocker}>{blocker}</li>
          ))}
        </ul>
      </div>
    </section>
  );
}

function LessonInternalPreviewPanel({
  courseRef,
  activeTenantId,
  enabled,
  lesson,
}: {
  courseRef: string;
  activeTenantId?: string | null;
  enabled: boolean;
  lesson: CourseStructureLesson;
}) {
  const encodedCourseRef = encodeURIComponent(courseRef);
  const encodedLessonRef = encodeURIComponent(lesson.id);
  const {
    data: preview,
    isLoading,
    error,
  } = useApi<CourseLessonPreviewResponse>(
    `/v1/courses/${encodedCourseRef}/lessons/${encodedLessonRef}/preview`,
    { ...(activeTenantId ? { activeTenantId } : {}), enabled },
  );

  if (!enabled) {
    return null;
  }

  if (isLoading) {
    return (
      <div className="mt-3 rounded-2xl border border-[var(--color-border)] bg-[var(--color-surface)] p-3">
        <Skeleton className="h-4 w-52" />
        <Skeleton className="mt-2 h-12 w-full" />
      </div>
    );
  }

  if (error || !preview) {
    return (
      <div className="mt-3 rounded-2xl border border-[var(--color-border)] bg-[var(--color-surface)] p-3 text-xs text-[var(--color-muted-foreground)]">
        Internal lesson preview is unavailable. Staff review can continue from structure and review-packet metadata only.
      </div>
    );
  }

  return (
    <div className="mt-3 rounded-2xl border border-[var(--color-border)] bg-[var(--color-surface)] p-3">
      <div className="flex flex-col gap-2 md:flex-row md:items-start md:justify-between">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.16em] text-[var(--color-muted-foreground)]">Internal lesson preview</p>
          <p className="mt-1 text-sm font-semibold text-[var(--color-foreground)]">
            {preview.decision === "READY_FOR_INTERNAL_LESSON_PREVIEW" ? "Safe block outline ready" : "Preview outline blocked"}
          </p>
          <p className="mt-1 text-xs text-[var(--color-muted-foreground)]">
            {preview.summary.blocks} blocks · {preview.summary.textPreviewBlocks} text snippets · {preview.summary.redactedFields} redacted fields
          </p>
        </div>
        <span className="rounded-full bg-background px-2 py-1 text-xs font-semibold text-[var(--color-foreground)]">
          {preview.previewMode}
        </span>
      </div>

      {preview.blockers.length > 0 ? (
        <p className="mt-3 text-xs text-[var(--color-warning)]">{preview.blockers.join(", ")}</p>
      ) : null}

      <div className="mt-3 grid gap-2 md:grid-cols-2">
        {preview.blocks.length === 0 ? (
          <p className="rounded-xl border border-dashed border-[var(--color-border)] bg-background p-3 text-xs text-[var(--color-muted-foreground)]">
            No safe preview blocks are available for this lesson.
          </p>
        ) : preview.blocks.slice(0, 4).map((block) => (
          <div key={`${block.index}-${block.type}`} className="rounded-xl bg-background p-3 text-xs">
            <div className="flex items-center justify-between gap-2">
              <span className="font-semibold text-[var(--color-foreground)]">{block.type}</span>
              <span className="text-[var(--color-muted-foreground)]">#{block.index + 1}</span>
            </div>
            <p className="mt-2 text-[var(--color-muted-foreground)]">
              {block.textPreview ?? "No safe text snippet exposed for this block."}
            </p>
            <p className="mt-2 text-[var(--color-muted-foreground)]">
              Media refs: {block.mediaReferenceCount} · redacted: {block.redactedFieldCount}
            </p>
          </div>
        ))}
      </div>
      {preview.blocks.length > 4 ? (
        <p className="mt-2 text-xs text-[var(--color-muted-foreground)]">{preview.blocks.length - 4} more blocks omitted from this compact view.</p>
      ) : null}
    </div>
  );
}

function LessonPageOperations({
  courseId,
  moduleId,
  lesson,
}: {
  courseId: string;
  moduleId: string;
  lesson: CourseStructureLesson;
}) {
  const page = lesson.contentPage;
  const lessonType = readCourseLessonType(lesson.lessonType);

  if (!page) {
    return (
      <div className="mt-3 rounded-2xl border border-dashed border-[var(--color-border)] bg-[var(--color-surface)] p-3 text-xs text-[var(--color-muted-foreground)]">
        No linked content page yet. Create/link flow is not mounted here yet, so this view will not invent a detached page.
      </div>
    );
  }

  if (!lessonType) {
    return (
      <div className="mt-3 rounded-2xl border border-[var(--color-border)] bg-[var(--color-surface)] p-3 text-xs text-[var(--color-muted-foreground)]">
        Unsupported lesson type for Creative Engine handoff: {lesson.lessonType}
      </div>
    );
  }

  const target = buildLessonOutputTarget({
    courseId,
    moduleId,
    lessonId: lesson.id,
    lessonType,
    title: lesson.title,
    pageId: page.id,
  });

  return (
    <div className="mt-3 flex flex-col gap-3 rounded-2xl border border-[var(--color-border)] bg-[var(--color-surface)] p-3 text-xs md:flex-row md:items-center md:justify-between">
      <div>
        <p className="font-semibold text-[var(--color-foreground)]">Linked page operations</p>
        <p className="mt-1 text-[var(--color-muted-foreground)]">
          Edit and review this {target.pageType} output through the mounted Page Builder workflow.
        </p>
      </div>
      <div className="flex flex-wrap gap-2">
        <Link
          to={buildCourseEditorHref(target)}
          className="rounded-full border border-[var(--color-border)] bg-background px-3 py-1.5 font-semibold text-[var(--color-foreground)] transition hover:border-[var(--color-primary)]"
        >
          Open editor
        </Link>
        <Link
          to={buildCourseEditorHref(target, "review")}
          className="rounded-full border border-[var(--color-border)] bg-background px-3 py-1.5 font-semibold text-[var(--color-foreground)] transition hover:border-[var(--color-primary)]"
        >
          Review page
        </Link>
      </div>
    </div>
  );
}

function CourseCatalogPage() {
  const [selectedCourseTenantId, setSelectedCourseTenantId] = useState(() => getStoredCourseTenantId());
  const {
    data: workspace,
    isLoading: isWorkspaceLoading,
    error: workspaceError,
  } = useApi<WorkspaceContextResponse>("/v1/platform/workspace-context", {
    skipActiveTenantHeader: true,
  });
  const activeTenant = selectCourseCatalogTenant(workspace, selectedCourseTenantId);
  const courseCatalogEnabled = Boolean(activeTenant?.id);
  const { data, isLoading, error } = useApi<CourseCatalogResponse>("/v1/courses?limit=50", {
    activeTenantId: activeTenant?.id,
    enabled: courseCatalogEnabled,
  });
  const switchableTenants = (workspace?.tenants ?? []).filter((tenant) => tenant.id !== activeTenant?.id);

  function handleCourseTenantSwitch(tenantId: string): void {
    setSelectedCourseTenantId(tenantId);
    setStoredCourseTenantId(tenantId);
  }

  useEffect(() => {
    if (!workspace) return;

    const tenants = workspace.tenants ?? [];
    const storedTenantId = getStoredCourseTenantId();
    const storedCourseTenantIsAccessible = storedTenantId
      ? tenants.some((tenant) => tenant.id === storedTenantId)
      : false;
    const nextTenantId = activeTenant?.id ?? (storedCourseTenantIsAccessible ? storedTenantId : null);

    if (nextTenantId !== storedTenantId) {
      setStoredCourseTenantId(nextTenantId);
      setSelectedCourseTenantId(nextTenantId);
    }
  }, [activeTenant?.id, workspace]);

  if (isWorkspaceLoading || (courseCatalogEnabled && isLoading)) {
    return (
      <div className="space-y-6 p-6">
        <Skeleton className="h-9 w-64" />
        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
          {Array.from({ length: 6 }).map((_, index) => (
            <Skeleton key={index} className="h-40 rounded-2xl" />
          ))}
        </div>
      </div>
    );
  }

  if (workspaceError || error || !data) {
    return (
      <div className="space-y-4 p-6">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[var(--color-muted-foreground)]">Courses</p>
          <h1 className="mt-2 text-3xl font-semibold text-[var(--color-foreground)]">Course Workspace</h1>
        </div>
        <div className="rounded-2xl border border-[var(--color-destructive)]/20 bg-[var(--color-destructive)]/5 p-5 text-sm text-[var(--color-destructive)]">
          Course catalog unavailable.
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 p-6">
      <div className="flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[var(--color-muted-foreground)]">Courses</p>
          <h1 className="mt-2 text-3xl font-semibold text-[var(--color-foreground)]">Course Workspace</h1>
          <p className="mt-2 max-w-2xl text-sm leading-6 text-[var(--color-muted-foreground)]">
            Tenant-scoped courses from Stero's canonical course read model. The frontend renders API state; it does not invent course data.
          </p>
        </div>
        <div className="rounded-2xl border border-[var(--color-border)] bg-background px-5 py-3 text-sm text-[var(--color-muted-foreground)]">
          <span className="font-semibold text-[var(--color-foreground)]">{data.counts.courses}</span> total courses
          <span className="mt-1 block text-xs">Tenant: {describeTenant(activeTenant)}</span>
        </div>
      </div>

      {switchableTenants.length > 0 ? (
        <div className="rounded-3xl border border-[var(--color-border)] bg-background p-5">
          <p className="text-xs font-semibold uppercase tracking-[0.16em] text-[var(--color-muted-foreground)]">Course tenant context</p>
          <div className="mt-3 flex flex-wrap gap-2">
            <span className="rounded-full border border-[var(--color-primary)] bg-[var(--color-primary)]/10 px-3 py-1.5 text-xs font-semibold text-[var(--color-foreground)]">
              {activeTenant ? `${activeTenant.name} (${activeTenant.slug})` : "No tenant selected"}
            </span>
            {switchableTenants.map((tenant) => (
              <button
                key={tenant.id}
                type="button"
                onClick={() => handleCourseTenantSwitch(tenant.id)}
                className="rounded-full border border-[var(--color-border)] bg-[var(--color-surface)] px-3 py-1.5 text-xs font-semibold text-[var(--color-muted-foreground)] transition hover:border-[var(--color-primary)] hover:text-[var(--color-foreground)]"
              >
                {tenant.name} ({tenant.slug})
              </button>
            ))}
          </div>
          <p className="mt-3 text-xs leading-5 text-[var(--color-muted-foreground)]">
            This selector only scopes course APIs. It does not change the global CRM/onboarding workspace tenant.
          </p>
        </div>
      ) : null}

      {data.items.length === 0 ? (
        <div className="rounded-3xl border border-dashed border-[var(--color-border)] bg-background p-8">
          <h2 className="text-xl font-semibold text-[var(--color-foreground)]">No courses found</h2>
          <p className="mt-2 max-w-xl text-sm leading-6 text-[var(--color-muted-foreground)]">
            Current tenant: <span className="font-semibold text-[var(--color-foreground)]">{describeTenant(activeTenant)}</span>. This tenant has no course records in the mounted course catalog yet. Once migration or course creation writes approved records, they will appear here through `/v1/courses`.
          </p>
          {switchableTenants.length > 0 ? (
            <div className="mt-5 space-y-3">
              <p className="text-xs font-semibold uppercase tracking-[0.16em] text-[var(--color-muted-foreground)]">Available tenant contexts</p>
              <div className="grid gap-3 md:grid-cols-2">
                {switchableTenants.map((tenant) => (
                  <button
                    key={tenant.id}
                    type="button"
                    onClick={() => handleCourseTenantSwitch(tenant.id)}
                    className="rounded-2xl border border-[var(--color-border)] bg-[var(--color-surface)] p-4 text-left transition hover:border-[var(--color-primary)]"
                  >
                    <span className="block text-sm font-semibold text-[var(--color-foreground)]">{tenant.name}</span>
                    <span className="mt-1 block text-xs text-[var(--color-muted-foreground)]">
                      {tenant.slug} · {tenant.status} · {tenant.permissionLevel}
                    </span>
                    <span className="mt-3 inline-flex text-xs font-semibold text-[var(--color-primary)]">Switch to this tenant</span>
                  </button>
                ))}
              </div>
            </div>
          ) : null}
        </div>
      ) : (
        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
          {data.items.map((course) => (
            <Link
              key={course.id}
              to={`course/${encodeURIComponent(course.id)}`}
              className="rounded-3xl border border-[var(--color-border)] bg-background p-5 shadow-sm transition hover:-translate-y-0.5 hover:shadow-md"
            >
              <div className="flex items-start justify-between gap-4">
                <div>
                  <p className="text-xs font-semibold uppercase tracking-[0.16em] text-[var(--color-muted-foreground)]">{course.slug}</p>
                  <h2 className="mt-2 text-xl font-semibold text-[var(--color-foreground)]">{course.title}</h2>
                </div>
                <span className="rounded-full border border-[var(--color-border)] px-3 py-1 text-xs font-medium capitalize text-[var(--color-foreground)]">
                  {course.status}
                </span>
              </div>

              {course.description ? (
                <p className="mt-3 line-clamp-2 text-sm leading-6 text-[var(--color-muted-foreground)]">{course.description}</p>
              ) : null}

              <dl className="mt-5 grid grid-cols-3 gap-2 text-sm">
                <div className="rounded-2xl bg-[var(--color-surface)] p-3">
                  <dt className="text-[11px] uppercase tracking-[0.14em] text-[var(--color-muted-foreground)]">Modules</dt>
                  <dd className="mt-1 text-lg font-semibold text-[var(--color-foreground)]">{course.moduleCount}</dd>
                </div>
                <div className="rounded-2xl bg-[var(--color-surface)] p-3">
                  <dt className="text-[11px] uppercase tracking-[0.14em] text-[var(--color-muted-foreground)]">Lessons</dt>
                  <dd className="mt-1 text-lg font-semibold text-[var(--color-foreground)]">{course.lessonCount}</dd>
                </div>
                <div className="rounded-2xl bg-[var(--color-surface)] p-3">
                  <dt className="text-[11px] uppercase tracking-[0.14em] text-[var(--color-muted-foreground)]">Pages</dt>
                  <dd className="mt-1 text-lg font-semibold text-[var(--color-foreground)]">{course.linkedPageCount}</dd>
                </div>
              </dl>
              <div className="mt-4 rounded-2xl border border-[var(--color-border)] bg-[var(--color-surface)] p-3 text-xs text-[var(--color-muted-foreground)]">
                <span className="font-semibold text-[var(--color-foreground)]">{course.externalVideoLessonCount}/{course.videoLessonCount}</span> video lessons have delivery assets
                {course.missingVideoAssetCount > 0 ? (
                  <span className="ml-2 text-[var(--color-warning)]">{course.missingVideoAssetCount} missing</span>
                ) : null}
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}

function CourseStructurePage() {
  const { courseRef } = useParams<{ courseRef: string }>();
  const { user } = useAuth();
  const canReadStaffCourseEvidence = canUseInternalLessonPreview(user?.role);
  const encodedCourseRef = encodeURIComponent(courseRef ?? "");
  const { data: workspace } = useApi<WorkspaceContextResponse>("/v1/platform/workspace-context", {
    skipActiveTenantHeader: true,
  });
  const courseTenantId = selectCourseCatalogTenant(workspace, getStoredCourseTenantId())?.id ?? getStoredCourseTenantId();
  const courseTenantOptions = courseTenantId ? { activeTenantId: courseTenantId } : undefined;
  const { data, isLoading, error } = useApi<CourseStructureResponse>(
    `/v1/courses/${encodedCourseRef}`,
    courseTenantOptions,
  );
  const {
    data: progress,
    isLoading: isProgressLoading,
    error: progressError,
  } = useApi<CourseProgressResponse>(`/v1/courses/${encodedCourseRef}/progress`, courseTenantOptions);
  const {
    data: learnerOperations,
    isLoading: isLearnerOperationsLoading,
    error: learnerOperationsError,
  } = useApi<CourseLearnerOperationsResponse>(
    `/v1/courses/${encodedCourseRef}/learner-operations`,
    { ...(courseTenantId ? { activeTenantId: courseTenantId } : {}), enabled: canReadStaffCourseEvidence },
  );
  const {
    data: capabilityMatrix,
    isLoading: isCapabilityMatrixLoading,
    error: capabilityMatrixError,
  } = useApi<CourseCapabilityMatrixResponse>(
    `/v1/courses/${encodedCourseRef}/capability-matrix`,
    { ...(courseTenantId ? { activeTenantId: courseTenantId } : {}), enabled: canReadStaffCourseEvidence },
  );
  const {
    data: readiness,
    isLoading: isReadinessLoading,
    error: readinessError,
  } = useApi<CourseReadinessResponse>(`/v1/courses/${encodedCourseRef}/readiness`, courseTenantOptions);
  const {
    data: certificateReadiness,
    isLoading: isCertificateReadinessLoading,
    error: certificateReadinessError,
  } = useApi<CourseCertificateReadinessResponse>(
    `/v1/courses/${encodedCourseRef}/certificate-readiness`,
    { ...(courseTenantId ? { activeTenantId: courseTenantId } : {}), enabled: canReadStaffCourseEvidence },
  );
  const {
    data: reviewPacket,
    isLoading: isReviewPacketLoading,
    error: reviewPacketError,
  } = useApi<CourseReviewPacketResponse>(`/v1/courses/${encodedCourseRef}/review-packet`, courseTenantOptions);
  const {
    data: videoDelivery,
    isLoading: isVideoDeliveryLoading,
    error: videoDeliveryError,
  } = useApi<CourseVideoDeliveryResponse>(`/v1/courses/${encodedCourseRef}/video-delivery`, courseTenantOptions);
  const {
    data: releaseCandidatePacket,
    isLoading: isReleaseCandidatePacketLoading,
    error: releaseCandidatePacketError,
  } = useApi<CourseReleaseCandidateReadPacketResponse>(
    `/v1/courses/${encodedCourseRef}/release-candidate-read-packet`,
    { ...(courseTenantId ? { activeTenantId: courseTenantId } : {}), enabled: canReadStaffCourseEvidence },
  );
  const {
    data: learnerServingPilotGate,
    isLoading: isLearnerServingPilotGateLoading,
    error: learnerServingPilotGateError,
  } = useApi<CourseLearnerServingPilotGateResponse>(
    `/v1/courses/${encodedCourseRef}/learner-serving-pilot-gate`,
    { ...(courseTenantId ? { activeTenantId: courseTenantId } : {}), enabled: canReadStaffCourseEvidence },
  );
  const {
    data: cutoverReadinessSnapshot,
    isLoading: isCutoverReadinessSnapshotLoading,
    error: cutoverReadinessSnapshotError,
  } = useApi<CourseCutoverReadinessSnapshotResponse>(
    `/v1/courses/${encodedCourseRef}/cutover-readiness-snapshot`,
    { ...(courseTenantId ? { activeTenantId: courseTenantId } : {}), enabled: canReadStaffCourseEvidence },
  );
  const {
    data: dualRunSmokePacket,
    isLoading: isDualRunSmokePacketLoading,
    error: dualRunSmokePacketError,
  } = useApi<CourseDualRunSmokePacketResponse>(
    `/v1/courses/${encodedCourseRef}/dual-run-smoke-packet`,
    { ...(courseTenantId ? { activeTenantId: courseTenantId } : {}), enabled: canReadStaffCourseEvidence },
  );

  if (isLoading) {
    return (
      <div className="space-y-6 p-6">
        <Skeleton className="h-8 w-40" />
        <Skeleton className="h-32 rounded-3xl" />
        <Skeleton className="h-48 rounded-3xl" />
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="space-y-4 p-6">
        <Link to=".." className="text-sm font-medium text-[var(--color-primary)]">Back to courses</Link>
        <div className="rounded-2xl border border-[var(--color-destructive)]/20 bg-[var(--color-destructive)]/5 p-5 text-sm text-[var(--color-destructive)]">
          Course structure unavailable.
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 p-6">
      <Link to=".." className="text-sm font-medium text-[var(--color-primary)]">Back to courses</Link>

      <section className="rounded-3xl border border-[var(--color-border)] bg-background p-6">
        <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[var(--color-muted-foreground)]">{data.course.slug}</p>
            <h1 className="mt-2 text-3xl font-semibold text-[var(--color-foreground)]">{data.course.title}</h1>
            {data.course.description ? (
              <p className="mt-3 max-w-3xl text-sm leading-6 text-[var(--color-muted-foreground)]">{data.course.description}</p>
            ) : null}
          </div>
          <span className="rounded-full border border-[var(--color-border)] px-3 py-1 text-xs font-medium capitalize text-[var(--color-foreground)]">
            {data.course.status}
          </span>
        </div>

        <dl className="mt-6 grid gap-3 text-sm md:grid-cols-4">
          <div className="rounded-2xl bg-[var(--color-surface)] p-4">
            <dt className="text-[11px] uppercase tracking-[0.14em] text-[var(--color-muted-foreground)]">Modules</dt>
            <dd className="mt-1 text-xl font-semibold text-[var(--color-foreground)]">{data.course.moduleCount}</dd>
          </div>
          <div className="rounded-2xl bg-[var(--color-surface)] p-4">
            <dt className="text-[11px] uppercase tracking-[0.14em] text-[var(--color-muted-foreground)]">Lessons</dt>
            <dd className="mt-1 text-xl font-semibold text-[var(--color-foreground)]">{data.course.lessonCount}</dd>
          </div>
          <div className="rounded-2xl bg-[var(--color-surface)] p-4">
            <dt className="text-[11px] uppercase tracking-[0.14em] text-[var(--color-muted-foreground)]">Pages</dt>
            <dd className="mt-1 text-xl font-semibold text-[var(--color-foreground)]">{data.course.linkedPageCount}</dd>
          </div>
          <div className="rounded-2xl bg-[var(--color-surface)] p-4">
            <dt className="text-[11px] uppercase tracking-[0.14em] text-[var(--color-muted-foreground)]">Access tiers</dt>
            <dd className="mt-1 text-sm font-semibold text-[var(--color-foreground)]">
              {data.course.accessTierIds.length > 0 ? data.course.accessTierIds.join(", ") : "None"}
            </dd>
          </div>
        </dl>
        <div className="mt-4 rounded-2xl border border-[var(--color-border)] bg-[var(--color-surface)] p-4 text-sm text-[var(--color-muted-foreground)]">
          <span className="font-semibold text-[var(--color-foreground)]">{data.course.externalVideoLessonCount}/{data.course.videoLessonCount}</span> video lessons have delivery assets mounted.
          {data.course.missingVideoAssetCount > 0 ? (
            <span className="ml-2 text-[var(--color-warning)]">{data.course.missingVideoAssetCount} video lessons still need an asset before serving.</span>
          ) : null}
        </div>
      </section>

      <ProgressSummaryPanel progress={progress} isLoading={isProgressLoading} error={progressError} />
      <LearnerOperationsPanel
        operations={learnerOperations}
        isLoading={isLearnerOperationsLoading}
        error={learnerOperationsError}
        enabled={canReadStaffCourseEvidence}
      />
      <CapabilityMatrixPanel
        matrix={capabilityMatrix}
        isLoading={isCapabilityMatrixLoading}
        error={capabilityMatrixError}
        enabled={canReadStaffCourseEvidence}
      />
      <ReadinessPanel readiness={readiness} isLoading={isReadinessLoading} error={readinessError} />
      <CertificateReadinessPanel
        readiness={certificateReadiness}
        isLoading={isCertificateReadinessLoading}
        error={certificateReadinessError}
        enabled={canReadStaffCourseEvidence}
      />
      <VideoDeliveryReviewPanel manifest={videoDelivery} isLoading={isVideoDeliveryLoading} error={videoDeliveryError} />
      <StaffReviewPacketPanel packet={reviewPacket} isLoading={isReviewPacketLoading} error={reviewPacketError} />
      <ReleaseCandidateReadPacketPanel
        packet={releaseCandidatePacket}
        isLoading={isReleaseCandidatePacketLoading}
        error={releaseCandidatePacketError}
        enabled={canReadStaffCourseEvidence}
      />
      <LearnerServingPilotGatePanel
        gate={learnerServingPilotGate}
        isLoading={isLearnerServingPilotGateLoading}
        error={learnerServingPilotGateError}
        enabled={canReadStaffCourseEvidence}
      />
      <DualRunSmokePacketPanel
        packet={dualRunSmokePacket}
        isLoading={isDualRunSmokePacketLoading}
        error={dualRunSmokePacketError}
        enabled={canReadStaffCourseEvidence}
      />
      <CutoverReadinessSnapshotPanel
        snapshot={cutoverReadinessSnapshot}
        isLoading={isCutoverReadinessSnapshotLoading}
        error={cutoverReadinessSnapshotError}
        enabled={canReadStaffCourseEvidence}
      />

      <section className="space-y-4">
        {data.modules.length === 0 ? (
          <div className="rounded-3xl border border-dashed border-[var(--color-border)] bg-background p-8">
            <h2 className="text-xl font-semibold text-[var(--color-foreground)]">No modules yet</h2>
            <p className="mt-2 text-sm text-[var(--color-muted-foreground)]">This course exists, but no module records are mounted in the tenant course model.</p>
          </div>
        ) : data.modules.map((module) => (
          <article key={module.id} className="rounded-3xl border border-[var(--color-border)] bg-background p-5">
            <div className="flex items-center justify-between gap-4">
              <h2 className="text-xl font-semibold text-[var(--color-foreground)]">{module.title}</h2>
              <span className="text-xs text-[var(--color-muted-foreground)]">{module.lessons.length} lessons</span>
            </div>
            <div className="mt-4 divide-y divide-[var(--color-border)]">
              {module.lessons.length === 0 ? (
                <p className="py-4 text-sm text-[var(--color-muted-foreground)]">No lessons in this module.</p>
              ) : module.lessons.map((lesson) => (
                <div key={lesson.id} className="py-4">
                  <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
                    <div>
                      <p className="text-sm font-semibold text-[var(--color-foreground)]">{lesson.title}</p>
                      <p className="mt-1 text-xs text-[var(--color-muted-foreground)]">
                        {lesson.lessonType}
                        {lesson.estimatedMinutes !== null ? ` - ${lesson.estimatedMinutes} min` : ""}
                        {lesson.contentPage?.title ? ` - ${lesson.contentPage.title}` : ""}
                      </p>
                    </div>
                    <div className="flex flex-wrap gap-2 text-xs text-[var(--color-muted-foreground)]">
                      {lesson.assets.videoDelivery ? (
                        <span className="rounded-full bg-[var(--color-surface)] px-2 py-1">
                          video: {formatVideoProvider(lesson.assets.videoDelivery.provider)}
                        </span>
                      ) : lesson.assets.hasExternalVideo ? (
                        <span className="rounded-full bg-[var(--color-surface)] px-2 py-1">video review needed</span>
                      ) : null}
                      {lesson.assets.hasPdf ? <span className="rounded-full bg-[var(--color-surface)] px-2 py-1">pdf</span> : null}
                      {lesson.assets.hasQuiz ? <span className="rounded-full bg-[var(--color-surface)] px-2 py-1">quiz</span> : null}
                      {lesson.contentPage ? <span className="rounded-full bg-[var(--color-surface)] px-2 py-1">{lesson.contentPage.status ?? "page"}</span> : null}
                    </div>
                  </div>
                  <VideoDeliveryPreview lesson={lesson} />
                  <LessonInternalPreviewPanel
                    courseRef={courseRef ?? data.course.id}
                    activeTenantId={courseTenantId}
                    enabled={canReadStaffCourseEvidence}
                    lesson={lesson}
                  />
                  <LessonPageOperations courseId={data.course.id} moduleId={module.id} lesson={lesson} />
                </div>
              ))}
            </div>
          </article>
        ))}
      </section>
    </div>
  );
}

function CourseBuilderDeferredPage() {
  return (
    <div className="mx-auto max-w-4xl space-y-6 px-6 py-10">
      <Link to=".." className="text-sm font-medium text-[var(--color-primary)]">Back to courses</Link>
      <section className="rounded-3xl border border-[var(--color-border)] bg-card p-8 shadow-sm">
        <p className="text-xs font-semibold uppercase tracking-[0.18em] text-[var(--color-muted-foreground)]">
          Course authoring gate
        </p>
        <h1 className="mt-3 text-3xl font-semibold text-[var(--color-foreground)]">
          API-backed course builder not mounted yet
        </h1>
        <p className="mt-4 max-w-2xl text-sm leading-6 text-[var(--color-muted-foreground)]">
          Stero course creation must write through the canonical course APIs. The previous local draft builder
          could create detached page-editor targets, so it stays blocked until a reviewed write contract exists.
        </p>
        <div className="mt-6 grid gap-3 text-sm sm:grid-cols-3">
          <div className="rounded-2xl bg-[var(--color-surface)] p-4">
            <p className="font-semibold text-[var(--color-foreground)]">Read model</p>
            <p className="mt-2 text-[var(--color-muted-foreground)]">Use the catalog and structure pages for mounted course data.</p>
          </div>
          <div className="rounded-2xl bg-[var(--color-surface)] p-4">
            <p className="font-semibold text-[var(--color-foreground)]">Write mode</p>
            <p className="mt-2 text-[var(--color-muted-foreground)]">Requires a separate reviewed API contract and operator gate.</p>
          </div>
          <div className="rounded-2xl bg-[var(--color-surface)] p-4">
            <p className="font-semibold text-[var(--color-foreground)]">Frontend rule</p>
            <p className="mt-2 text-[var(--color-muted-foreground)]">No local-only course records or draft-course fallbacks.</p>
          </div>
        </div>
      </section>
    </div>
  );
}

/**
 * Courses module — catalog first, authoring remains blocked until canonical write APIs exist.
 */
export default function CoursesPage() {
  return (
    <Routes>
      <Route index element={<CourseCatalogPage />} />
      <Route path="builder" element={<CourseBuilderDeferredPage />} />
      <Route path="course/:courseRef" element={<CourseStructurePage />} />
      <Route path="*" element={<Navigate to="." replace />} />
    </Routes>
  );
}
